import {Feature, FeatureCollection, LineString, MultiLineString, MultiPolygon, Point, Polygon} from 'geojson';
import rdToWgs84 from 'rd-to-wgs84';
import * as turf from '@turf/turf';
import {simplify} from '@turf/turf';
import {TtmTypes} from '../../../../frontend/src/app/classes/ttm-types.enum.js';
import {Drawing} from '../../../../frontend/src/app/classes/drawing.js';
import {MapEntity} from '../../../../frontend/src/app/classes/map-entity.js';
import {ImportEntity} from '../../../../frontend/src/app/classes/import.entity.js';
import {EntityToSave} from '../../../../frontend/src/app/classes/entity-to-save.js';
import {Ttm} from '../../../../frontend/src/app/classes/ttm.js';
import {TtmType} from '../../../../frontend/src/app/classes/ttmtype.js';

export class Converter {
    data: {
        text: string,
        connections?: ImportEntity[][],
        firstLines?: ImportEntity[],
        bord?: ImportEntity,
        location?: ImportEntity,
        otherPoints?: ImportEntity[]
    }[] = [];
    flattened: ImportEntity[];
    drawing: Drawing;

    staysOnOwnLocation = [
        'schild',
        '_cube',
        'keg1',
        'keg2',
        'keg3',
        'keg4',
        'schild1',
        'schild2',
        'schild3',
        'schild4',
        'schild-as',
        '5mat_dyn',
        '4mat_dyn',
        '3mat_dyn',
        '2mat_dyn',
        'vkr',
        'andreas',
        'afplak-afdraai-verwijder',
        '_afwijking'
    ];

    bordenForExport: MapEntity[] = [];
    otherItemsForExport: MapEntity[] = [];
    xrefs: ImportEntity[] = [];

    endPointSpecificNames = [
        '_aktiewb', 'mrsb', 'mrsb3', '_Botserb', '_botserb'
    ];

    // getallen in een cirkel op de kaart die refereren aan een bord
    referenceNr = ['_nr_p', '_nr', '_afwijking', 'IcoVooraank'];


    allowedTypes = [
        '_aktiew',
        '_tekstw',
        '_splitsk',
        '_verdrk',
        '_cube',
        'keg',
        'cbord',
        '_afzh',
        'rvv',
        '_rb',
        'rb',
        'vr',
        'schild',
        'besto',
        'drukknop',
        '_wiu',
        'aanwezige-bebording',
        '_nr_p',
        '_nr_a',
        'best',
        'IcoVooraank',
        '_mip',
        'b0rd',
        '_raamws',
        'portdynamisch',
        'bort',
        '5MAT_DYN',
        '4MAT_DYN',
        '3MAT_DYN',
        '2MAT_DYN',
        '1MAT_DYN',
        'PORT5MAT',
        'PORT4MAT',
        'PORT3MAT',
        'PORT2MAT',
        'PORT1MAT',
        'bord',
        'Bord',
        'reflector',
        'BW',
        'BB01rt',
        '_botser',
        'bm',
        'Block',
        'slagboom',
        '_inut',
        'Mp3'
    ];

    dynLocationNames = ['_HEK-dot-dyn', '_HEK_dot-dyn', '_BRD-dot-dyn', '_BRD_dot-dyn'];

    // lagen uitzonderen voor het zoeken naar locatielijnen
    excludedSearch = [
        'wy-kleur-253', 'vp1', 'dd00g007', 'markering ononderbroken', 'atr01', 'wy-kleur-030', 'wx-zwart-008', 'topografie', 'hulplijn', 'viewport', 'werkvak', 'rio dwa tekst',
        'werkvak (omleidingen)', 'markering pijlen - picto ed', 'barrier HaiSafe', 'barrier Bever', 'barrier RWS', 'wy-kleur-150', 'ATR01', 'hek (bouwhek)', 'tijdelijke verharding', 'wx-zwart-004', 'lock', '136', 'autoturn',
        'DefPoints', 'to-xx-kader', 'to-xx-rooster', 'to-xx-snijrand', 'to-xx-vouwmerk', 'zz-9102_ly – checklist', 'wx-zwart-001', 'B-WE-VH-VERHARDING_ASFALT-GV', 'best_bebording_np',
        'geluidsscherm', 'rio dwa inspectieput', 'grens bestaand', 'rio hwa inspectieput ontlast', 'sym-dik', 'wy-kleur-200', 'wy-kleur-040'
    ];
    // lagen uitzonderen als dit voorkomt in de naam voor het zoeken naar locatielijnen
    excludedSearchIfContains = [
        '-pastel$', 'past', 'geluidsscherm', 'verh_soort', 'mk-bs', 'z2bn0', 'inspectieput', 'rio hwa',
        'sym-dik', 'topo', 'leiding', 'betonband', 'viewport', 'geleiderail', 'barrier', 'markering'
    ];

    // deze items altijd uitsluiten
    excludedEntityNames = [
        'wopuntdoorsnede', '_wcnoord', 'wipe1', 'paal', 'z2bn0168', 'bladverwijzing', 'z2bn0121', 'menneke',
        '_parkeerverbodentsned-app', '_stepbb-750_geluidsscherm', 'a_daf_85', 'a_peugeot207', 'achevroletambulance',
        'a_audia8', 'gel080', 'sal-orientatie_noordpijl-so', 'boorrommel', '_neutraal', '_rijrichtingdrsnboven',
        '_rijrichtingdrsnboven', '_rijrichtingdrsnboven', 'z2bn0151', 'z2bn0160', 'stripke', '_tob-detail-dyn-v1ck01',
        'bak-nw', '_kruis', 'bak-rbu1', '_rijrichtingdrsnonder', 'boringmaterieel', 'haitsma-step barrier', 'minigraver',
        'bergje', 'ns_logo', 'afplakken', 'lantaarnpaal'
    ];

    // items die de volgende tekst in de naam hebben uitsluiten.
    excludedEntityIfNameContains = [
        'a$c', 'pastel$'
    ];

    excludedEntityLayers = ['hulplijn'];

    bordEntities: ImportEntity[];

    coordinatesMovedBorders: MovedCoordinatesBoundary[] = [];
    coordinatesExcludedBorders: ExcludedCoordinatesBoundary[] = [];

    constructor(disableLogging = false) {
        if (disableLogging) {
            console.clear();
            console.info = function () {
            };
            console.warn = function () {
            };
        }
        for (let i = 0; i <= 99; i++) {
            this.excludedSearch.push(`ATR${i < 10 ? '0' : ''}${i}`);
        }

    }

    isValidRDCoordinate(x: number, y: number): boolean {
        if (!x || !y) {
            return false;
        }
        const xMin = 12;
        const xMax = 280;
        const yMin = 300;
        const yMax = 620;
        return x >= xMin && x <= xMax && y >= yMin && y <= yMax;
    }

    isPointInPolygon(point: number[], polygon: number[][]): boolean {
        let inside = false;
        const x = point[0], y = point[1];
        const len = polygon.length;

        for (let i = 0, j = len - 1; i < len; j = i, i++) {
            const xi = polygon[i][0], yi = polygon[i][1];
            const xj = polygon[j][0], yj = polygon[j][1];

            const intersect = ((yi > y) !== (yj > y)) &&
                (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

            if (intersect) inside = !inside;
        }

        return inside;
    }

    isInExcludedBoundary(item: ImportEntity): boolean {
        let inBoundary = false;
        if (this.coordinatesExcludedBorders?.length) {
            this.coordinatesExcludedBorders.forEach(boundary => {
                const x = item.x ?? (item.start ? item?.start[0] / 1000 : null);
                const y = item.y ?? (item.start ? item?.start[1] / 1000 : null);

                if (inBoundary || this.isPointInPolygon([x, y], boundary.boundary)) {
                    inBoundary = true;
                }
            });
        }
        return inBoundary;
    }

    isInMovedBoundary(item: ImportEntity): MovedCoordinatesBoundary {
        let inBoundary = null;
        if (this.coordinatesMovedBorders?.length) {
            this.coordinatesMovedBorders.forEach(boundary => {
                const x = (item.x ?? (item.start ? item?.start[0] : null));
                const y = (item.y ?? (item.start ? item?.start[1] : null));
                if (inBoundary || this.isPointInPolygon([x, y], boundary.boundary)) {
                    inBoundary = inBoundary ?? boundary;
                }
            });
        }
        return inBoundary;
    }

    moveByBoundary(importEntity: ImportEntity) {
        const boundary = this.isInMovedBoundary(importEntity);
        const move = (item) => {
            if (!item.moved) {
                item.moved = boundary;
                if (item.x) {
                    item.x -= (boundary.movedX);
                    item.y -= (boundary.movedY);
                }
                const movePoint = (point?: [number, number, number?, number?]) => {
                    if (point) {
                        point[0] -= boundary.movedX;
                        point[1] -= boundary.movedY;
                    }
                };
                movePoint(item.start);
                movePoint(item.end);
                item.points?.forEach(p => movePoint(p));
                movePoint(item.block_min);
                movePoint(item.block_max);
            }
        };

        if (boundary) {
            move(importEntity);
            importEntity.entities?.forEach((subEntity) => {
                move(subEntity);
                subEntity.entities?.forEach((subSubEntity) => {
                    move(subSubEntity);
                });
            });
        }
        return importEntity;
    }

    doConvert(rawDataFromDxf: ImportEntity[], ttmTypes: TtmType[]) {
        if (!rawDataFromDxf) {
            console.error('No data from Dxf');
            return false;
        }
        if (!ttmTypes) {
            console.error('No ttmTypes');
            return false;
        }

        const xrefs = rawDataFromDxf.filter(raw => raw.type === 'XREF');
        rawDataFromDxf = rawDataFromDxf.filter(raw => raw.type !== 'XREF');
        this.xrefs = xrefs;

        rawDataFromDxf = rawDataFromDxf.filter(raw => {
            return !raw.name?.toLowerCase()?.startsWith('past') &&
                !raw.name?.toLowerCase()?.startsWith('plandef_') &&
                !raw?.layer.toLowerCase().startsWith('pdf') &&
                !raw.name?.toLowerCase()?.includes('bgt') &&
                !(raw.entities?.length && raw.entities[0]?.layer.toLowerCase().includes('pdf')) &&
                !this.excludedEntityNames.includes(raw.name?.toLowerCase().trim()) &&
                !this.excludedEntityIfNameContains.some(e => raw.name?.toLowerCase().includes(e.toLowerCase()));
        });
        const allowedTypes = this.allowedTypes;
        let flattenedEntities = rawDataFromDxf.flatMap(({entities, handle}) => entities?.map(entity => {
            entity.parent_handle = handle;
            return entity;
        }));
        const largeHandles = flattenedEntities.filter(f => f?.entities?.length > 100).map(f => f?.handle);
        flattenedEntities = flattenedEntities
            .filter(f => !f?.layer.startsWith('PDF'))
            .filter(f => largeHandles.indexOf(f?.handle) === -1)
            .concat(...rawDataFromDxf.filter(rdf => (largeHandles.indexOf(rdf?.parent_handle) === -1) && largeHandles.indexOf(rdf?.handle) === -1))
            .filter(e => !!e)
            .filter(e => e.type !== 'MTEXT' || !e.parent_handle)
            .filter(f => !['WIPEOUT', 'ATTDEF'].includes(f.type));

        flattenedEntities.filter(f => f.name === '_parkeerverbodenTSNed-app').forEach(f => {
            const boundary = f.entities.find(e => e.type === 'LWPOLYLINE' && e.points?.length > 3);
            if (!boundary) {
                console.error('parkeerverbodenvak zonder grenslijn');
                return;
            }
            this.coordinatesMovedBorders.push({
                boundary: boundary.points,
                movedX: isNaN(f.attr['VERPLAATSING-X']) ? 0 : +f.attr['VERPLAATSING-X'],
                movedY: isNaN(f.attr['VERPLAATSING-Y']) ? 0 : +f.attr['VERPLAATSING-Y']
            });
        });
        flattenedEntities.filter(f => f.name === '_verplaatsingTSNed-app').forEach(f => {
            const boundary = f.entities.find(e => e.type === 'LWPOLYLINE' && e.points?.length > 3);
            if (!boundary) {
                console.error('verplaatsingsvak zonder grenslijn');
                return;
            }
            this.coordinatesMovedBorders.push({
                boundary: boundary.points,
                movedX: isNaN(f.attr['VERPLAATSING-X']) ? 0 : +`${f.attr['VERPLAATSING-X']}`.replace(/[^\d-]/g, ''),
                movedY: isNaN(f.attr['VERPLAATSING-Y']) ? 0 : +`${f.attr['VERPLAATSING-Y']}`.replace(/[^\d-]/g, '')
            });
        });

        console.info(this.coordinatesMovedBorders);

        flattenedEntities.filter(f => f.name === '_uitgeslotenTSNed-app').forEach(f => {
            const boundary = f.entities.find(e => e.type === 'LWPOLYLINE' && e.points?.length > 3);
            this.coordinatesExcludedBorders.push({
                boundary: boundary.points
            });
        });

        flattenedEntities = flattenedEntities.filter(item => {
            //todo als er een kader is voor parkeerborden hier  -- this.isInExcludedBoundary(item) -- weer meenemen
            return !item.name?.includes('TSNed-app') && !item.layer.includes('-uitgesloten') && !item.layer.includes('-verplaatsing');
        });

        flattenedEntities.forEach(entity => {
            //controleer of entity in verplaatsingsvak valt
            this.moveByBoundary(entity);
        });

        // Bereken de offset voor paperspace coordinaten om geldige RD coordinaten te krijgen
        let paperSpaceOffsetX = 100000;
        let paperSpaceOffsetY = 300000;

        // Verzamel alle paperspace coordinaten om de bounds te bepalen
        const paperSpaceEntities = flattenedEntities.filter(e => e.space === 'paperspace');
        if (paperSpaceEntities.length > 0) {
            let minX = Infinity;
            let maxX = -Infinity;
            let minY = Infinity;
            let maxY = -Infinity;

            paperSpaceEntities.forEach(entity => {
                if (entity.x) {
                    minX = Math.min(minX, entity.x);
                    maxX = Math.max(maxX, entity.x);
                    minY = Math.min(minY, entity.y);
                    maxY = Math.max(maxY, entity.y);
                }
                entity.points?.forEach((point) => {
                    minX = Math.min(minX, point[0]);
                    maxX = Math.max(maxX, point[0]);
                    minY = Math.min(minY, point[1]);
                    maxY = Math.max(maxY, point[1]);
                });
                if (entity.block_min) {
                    minX = Math.min(minX, entity.block_min[0]);
                    maxX = Math.max(maxX, entity.block_max[0]);
                    minY = Math.min(minY, entity.block_min[1]);
                    maxY = Math.max(maxY, entity.block_max[1]);
                }
            });

            // Bereken offset zodat alle coordinaten binnen valide RD range vallen
            // Valide RD range in meters: X: 12000-280000, Y: 300000-620000
            const RD_X_MIN = 12000;
            const RD_X_MAX = 280000;
            const RD_Y_MIN = 300000;
            const RD_Y_MAX = 620000;

            if (isFinite(minX) && isFinite(maxX) && isFinite(minY) && isFinite(maxY)) {
                const rangeX = maxX - minX;
                const rangeY = maxY - minY;

                // Bereken offset zodat de coordinaten gecentreerd in de valide range vallen
                // met voldoende marge aan beide kanten
                paperSpaceOffsetX = Math.floor((RD_X_MIN + RD_X_MAX) / 2 - (minX + maxX) / 2);
                paperSpaceOffsetY = Math.floor((RD_Y_MIN + RD_Y_MAX) / 2 - (minY + maxY) / 2);

                // Controleer of met deze offset alle coordinaten binnen range blijven
                const correctedMinX = (minX + paperSpaceOffsetX) / 1000;
                const correctedMaxX = (maxX + paperSpaceOffsetX) / 1000;
                const correctedMinY = (minY + paperSpaceOffsetY) / 1000;
                const correctedMaxY = (maxY + paperSpaceOffsetY) / 1000;

                // Pas offset aan als we buiten de range komen
                if (correctedMinX < RD_X_MIN / 1000) {
                    paperSpaceOffsetX += (RD_X_MIN / 1000 - correctedMinX + 1) * 1000;
                }
                if (correctedMaxX > RD_X_MAX / 1000) {
                    paperSpaceOffsetX -= (correctedMaxX - RD_X_MAX / 1000 + 1) * 1000;
                }
                if (correctedMinY < RD_Y_MIN / 1000) {
                    paperSpaceOffsetY += (RD_Y_MIN / 1000 - correctedMinY + 1) * 1000;
                }
                if (correctedMaxY > RD_Y_MAX / 1000) {
                    paperSpaceOffsetY -= (correctedMaxY - RD_Y_MAX / 1000 + 1) * 1000;
                }
            }
        }

        flattenedEntities.forEach(entity => {

            // als paperspace, geen geldige coordinaten, om het werken met turf makkelijk te maken worden er geldige RD coordinaten van gemaakt die omgezet kunnen worden naar wgs
            if (entity.space === 'paperspace') {

                const alterCoordinates = (e: ImportEntity) => {
                    if (e.x) {
                        e.x += paperSpaceOffsetX;
                        e.y += paperSpaceOffsetY;
                    }
                    e.points?.forEach((point) => {
                        point[0] += paperSpaceOffsetX;
                        point[1] += paperSpaceOffsetY;
                    });
                    if (e.block_min) {
                        e.block_min[0] += paperSpaceOffsetX;
                        e.block_min[1] += paperSpaceOffsetY;
                    }
                    if (e.block_max) {
                        e.block_max[0] += paperSpaceOffsetX;
                        e.block_max[1] += paperSpaceOffsetY;
                    }
                    if (e.entities?.length) {
                        e.entities.forEach(ent => {
                            alterCoordinates(ent);
                        })
                    }
                };

                alterCoordinates(entity);
            }

            // Dimstyle en ATTDEF hebben geen coordinaten
            if (entity && entity.x && entity.y && entity.name !== 'Dimstyle' && entity.type !== 'ATTDEF') {
                const entityX = entity.x / 1000;
                const entityY = entity.y / 1000;
                if (this.isValidRDCoordinate(entityX, entityY)) {
                    const wgsCoordinates = rdToWgs84(entityX, entityY);
                    if (wgsCoordinates.lat && wgsCoordinates.lon) {
                        entity.lat = wgsCoordinates.lat;
                        entity.lng = wgsCoordinates.lon;
                    } else {
                        console.error('fout bij rd conversie', entity, wgsCoordinates);
                    }
                }
            }
            if (entity?.block_min && entity?.block_max && this.isValidRDCoordinate(entity.block_min[0] / 1000, entity.block_min[1] / 1000) && this.isValidRDCoordinate(entity.block_max[0] / 1000, entity.block_max[1] / 1000)) {
                const wgsCoordinatesMin = rdToWgs84(entity.block_min[0], entity.block_min[1]);
                entity.block_min_lat = wgsCoordinatesMin.lat;
                entity.block_min_lng = wgsCoordinatesMin.lon;
                const wgsCoordinatesMax = rdToWgs84(entity.block_max[0], entity.block_max[1]);
                entity.block_max_lat = wgsCoordinatesMax.lat;
                entity.block_max_lng = wgsCoordinatesMax.lon;
            }
            const points = entity.points?.filter(p => this.isValidRDCoordinate(p[0] / 1000, p[1] / 1000));
            entity.wgsPoints = points?.map(p => {
                const wgsCoordinates = rdToWgs84(p[0] / 1000, p[1] / 1000);
                return {
                    lat: wgsCoordinates.lat,
                    lng: wgsCoordinates.lon
                };
            });

            if (entity.start && entity.end && this.isValidRDCoordinate(entity.start[0] / 1000, entity.start[1] / 1000) && this.isValidRDCoordinate(entity.end[0] / 1000, entity.end[1] / 1000)) {
                const latStart = rdToWgs84(entity.start[0] / 1000, entity.start[1] / 1000);
                const latEnd = rdToWgs84(entity.end[0] / 1000, entity.end[1] / 1000);

                entity.wgsStart = {
                    lat: latStart.lat,
                    lng: latStart.lon
                };
                entity.wgsEnd = {
                    lat: latEnd.lat,
                    lng: latEnd.lon
                };
            }
            if (entity.lines?.length && !entity.points?.length) {
                if (this.isValidRDCoordinate(entity.lines[0][0][0] / 1000, entity.lines[0][0][1] / 1000)) {
                    const wgsLines = [];
                    entity.lines.forEach(line => {
                        wgsLines.push(line.map(point => {
                            const wgsCoordinates = rdToWgs84(point[0] / 1000, point[1] / 1000);
                            return {
                                lat: wgsCoordinates.lat,
                                lng: wgsCoordinates.lon
                            };
                        }));
                    });
                    entity.wgsLines = wgsLines;
                }
            }
        });
        console.info('wgs conversion complete');

        function calculateWidth(latitude: number, height: number): number {
            // Converteer breedtegraad naar radialen
            const latitudeInRadians = (latitude * Math.PI) / 180;

            // Bereken de schaalfactor op basis van cosinus
            const scaleFactor = Math.cos(latitudeInRadians);

            // Bereken de vereiste breedte
            return height * scaleFactor;
        }

        flattenedEntities.forEach(entity => {
            if (['HATCH', 'CIRCLE'].includes(entity?.type)) {
                if (entity.radius < 1 && entity?.lat) {
                    let scale = 1;
                    if (entity.parent_handle) {
                        scale = flattenedEntities.find(f => f.handle === entity.parent_handle)?.xScale ?? 1;
                    }
                    if (entity.radius > 0) {
                        const diameter = (entity.radius * scale) * 1.75; // 1,75 is extra scale om cirkel groot genoeg te maken om aansluitende lijn altijd te kunnen raken
                        entity.polygon = turf.ellipse(turf.point([entity.lng, entity.lat]), calculateWidth(entity.lat, diameter), diameter, {units: 'meters'});


                        entity.wgsPointsPolygon = entity.polygon.geometry.coordinates[0].map(c => {
                            return {lng: c[0], lat: c[1]};
                        });
                    }
                }
                if (entity.wgsLines?.length) {
                    try {
                        const lineTuple = entity.wgsLines.map(l => l.map(p => [p.lng, p.lat]));
                        const multiLine = turf.multiLineString(lineTuple);
                        if (entity.lines?.length < 500) { // hang op grote polygons, daarom uitsluiten (sommige werkvakken)
                            entity.polygon = turf.polygonize(multiLine);
                        }
                        if (!entity.polygon?.features?.length) {
                            const lines = entity.wgsLines.flatMap(l => l.map(p => [p.lng, p.lat]));
                            const lineString = turf.cleanCoords(turf.lineString(lines));
                            entity.polygon = turf.lineToPolygon(lineString);
                        }
                    } catch (e) {
                        console.info(entity?.handle, 'cannot create polygon from lines');
                    }
                }
            }
            if (['LWPOLYLINE', 'LINE', 'LEADER'].includes(entity?.type)) {
                if (entity.has_arc && !entity.parent_handle) {
                    function coordinatesAreEqual(a, b) {
                        // Eenvoudige vergelijking op 6 decimalen, want floating point problemen
                        return Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6;
                    }

                    function lineArc(center, radius, startAngle, endAngle, steps = 10, startCoord, endCoord) {
                        // Hoekdelta normaliseren naar kortste weg (dus van 179° naar -179° is NIET bijna een hele cirkel!)
                        function normalizeAngleDelta(start, end) {
                            let delta = end - start;
                            if (delta > 180) delta -= 360;
                            if (delta < -180) delta += 360;
                            return delta;
                        }

                        const points = [];
                        const deltaAngle = normalizeAngleDelta(startAngle, endAngle);

                        for (let i = 0; i <= steps; i++) {
                            const angle = startAngle + (deltaAngle * (i / steps));
                            const theta = angle * Math.PI / 180;
                            const x = center[0] + radius * Math.cos(theta);
                            const y = center[1] + radius * Math.sin(theta);
                            points.push([x, y]);
                        }

                        // Check richting van de boog t.o.v. de opgegeven start en end
                        function dist(a, b) {
                            return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);
                        }

                        if (startCoord && endCoord) {
                            const dStart = dist(points[0], startCoord);
                            const dEnd = dist(points[points.length - 1], startCoord);
                            if (dEnd < dStart) {
                                points.reverse();
                            }
                            // Zorg dat de eerste en laatste punt precies gelijk zijn aan de opgegeven start/end
                            points[0] = startCoord;
                            points[points.length - 1] = endCoord;
                        }
                        return points;
                    }

                    function arcsToLineString(arcs) {
                        let coords = [];

                        arcs.forEach((arc, i) => {
                            if (arc.type === "line") {
                                if (coords.length === 0) {
                                    coords.push(rdToWgs84(arc.start[0], arc.start[1]));
                                }
                                const endWgs = rdToWgs84(arc.end[0], arc.end[1]);
                                if (!coordinatesAreEqual(coords[coords.length - 1], endWgs)) {
                                    coords.push(endWgs);
                                }
                            } else if (arc.type === "arc") {
                                let arcPoints = lineArc(
                                    arc.center,
                                    arc.radius,
                                    arc.start_angle,
                                    arc.end_angle,
                                    64,
                                    arc.start,
                                    arc.end
                                );

                                // arcs zijn soms omgekeerd, controleren, en omdraaien als nodig
                                if (arcs[i + 1]) {
                                    if (arcs[i + 1]?.type === 'line' && Math.abs(arc.end[0] - arcs[i + 1].start[0]) > 0.000006 && Math.abs(arc.end[1] - arcs[i + 1].start[1]) > 0.000006) {
                                        arcPoints = arcPoints.reverse();
                                    }
                                    if (arcs[i + 1]?.type === 'arc' && Math.abs(arc.start[0] - arcs[i + 1].start[0]) > 0.000006 && Math.abs(arc.start[1] - arcs[i + 1].start[1]) > 0.000006) {
                                        arcPoints = arcPoints.reverse();
                                    }
                                } else { // dit gebeurd ook weleens bij de laatste
                                    if (arcs[i - 1]?.type === 'line' && Math.abs(arc.start[0] - arcs[i - 1].end[0]) > 0.000006 && Math.abs(arc.start[1] - arcs[i - 1].end[1]) > 0.000006) {
                                        arcPoints = arcPoints.reverse();
                                    }
                                    if (arcs[i - 1]?.type === 'arc' && Math.abs(arc.end[0] - arcs[i - 1].end[0]) > 0.000006 && Math.abs(arc.end[1] - arcs[i - 1].end[1]) > 0.000006) {
                                        arcPoints = arcPoints.reverse();
                                    }
                                }
                                // Voeg toe, skip eerste punt als hij gelijk is aan coords[-1]
                                for (let j = 0; j < arcPoints.length; j++) {
                                    const wgs = rdToWgs84(arcPoints[j][0], arcPoints[j][1]);
                                    if (coords.length === 0 || !coordinatesAreEqual(coords[coords.length - 1], wgs)) {
                                        coords.push(wgs);
                                    }
                                }
                            }
                        });
                        return coords;
                    }

                    const coords = arcsToLineString(entity.arcs);
                    entity.wgsPoints = coords.map(c => {
                        return {lat: c.lat, lng: c.lon};
                    });
                } else {
                    if (!entity.wgsPoints && entity.wgsStart && entity.wgsEnd && !(entity.start[0] == entity.end[0] && entity.start[1] == entity.end[1])) {
                        entity.wgsPoints = [entity.wgsStart, entity.wgsEnd];
                    }
                }
                const wgsPoints = entity.wgsPoints?.filter(p => !!p.lat && !!p.lng);
                if (wgsPoints?.length > 1) {
                    const poly = turf.buffer(turf.lineString(wgsPoints.map(p => [p.lng, p.lat])), 0.22, {units: 'meters'});//beter 0.22 tot .33 en later ophogen als geen gewenst resultaat
                    entity.wgsPointsPolygon = poly.geometry.coordinates[0].map(c => {
                        return {lng: c[0], lat: c[1]};
                    });

                    entity.polygon = poly;
                }
            }
            if (entity?.type === 'INSERT') {
                const points = this.entityToPoints(entity.entities);
                const poly = turf.convex(turf.featureCollection(points));
                if (poly) {
                    let buffer = 25;
                    if (entity.name?.toLowerCase() === '_botserb') {
                        buffer = 150;
                    }
                    entity.polygon = turf.buffer(poly, buffer, {units: 'centimeters'});
                    entity.original_polygon = poly;
                } else if (entity.space === 'paperspace' && entity.block_min_lng) {
                    entity.polygon = turf.bboxPolygon([entity.block_min_lng, entity.block_min_lat, entity.block_max_lng, entity.block_max_lat]);
                } else if (entity.space === 'paperspace' && !entity.polygon && entity.lat) {
                    entity.polygon = turf.buffer(turf.point([entity.lng, entity.lat]), 1, {units: 'meters'});
                }
            }
            if (entity?.type === 'MTEXT' && entity?.space === 'paperspace' && entity.block_min_lng) {
                entity.polygon = turf.bboxPolygon([entity.block_min_lng, entity.block_min_lat, entity.block_max_lng, entity.block_max_lat]);
            }
            if (['MTEXT', 'TEXT'].includes(entity?.type) && entity?.space === 'modelspace') {
                const polyWgs = entity.poly?.map(p => {
                    const wgsCoordinates = rdToWgs84(p[0] / 1000, p[1] / 1000);
                    return {
                        lat: wgsCoordinates.lat,
                        lng: wgsCoordinates.lon
                    };
                });
                if (polyWgs && polyWgs[0].lng) {
                    entity.polygon = turf.buffer(turf.lineToPolygon(turf.lineString(polyWgs.map(p => [p.lng, p.lat]))), 3, {units: 'meters'});
                }
            }
        });

        //todo voor nu, uitgezonderde items hier pas filteren
        const allFlattenedEntities = flattenedEntities;
        flattenedEntities = flattenedEntities.filter(item => {
            return !this.isInExcludedBoundary(item) && item.space !== 'paperspace';
        });
        console.info('polygon creation complete');

        this.flattened = flattenedEntities.filter(e => e.type !== 'INSERT' || (!!e.lat && !!e.lng));
        const bordenToSearch = rawDataFromDxf
            .filter(e => e.type !== 'INSERT' || (!!e.lat && !!e.lng))
            .filter(item => {
                return !this.isInExcludedBoundary(item) &&
                    item.name !== '_uitgeslotenTSNed-app' &&
                    !this.excludedEntityLayers.includes(item.layer?.toLowerCase().trim()) &&
                    !item.layer.includes('-uitgesloten') &&
                    !item.layer.includes('-verplaatsing');
            }).filter(bord => {

                let allowed = false;
                allowedTypes.forEach(allowedType => {

                    if (bord.name?.substring(0, allowedType.length).toLowerCase() === allowedType.toLowerCase() && !this.endPointSpecificNames.includes(bord.name.toLowerCase())) {
                        allowed = true;
                    }
                    if (bord.type === 'INSERT' && bord.name?.substring(0, 2) === '*U' && Object.values(bord.attr)?.length > 0) {
                        allowed = true;
                    }
                });
                // bundeled
                bord.entities?.forEach((subEntity) => {
                    allowedTypes.forEach(allowedType => {
                        if (subEntity.name?.substring(0, allowedType.length) === allowedType) {
                            allowed = true;
                        }
                    });
                });
                return allowed;
            });
        this.bordEntities = bordenToSearch;
        console.info('bordenToSearch list complete');

        const searchForFirstConnectionsList = flattenedEntities.filter(entity => {
            if (entity.parent_handle && !!bordenToSearch.find(b => b.handle === entity.parent_handle)) {
                return false;
            }
            if (entity.layer === 'wy-kleur-010') {
                return false;
            }
            return true;
        });

        console.info('start searching');
        bordenToSearch.forEach(bord => {
            if (bord.type === 'INSERT' && (this.staysOnOwnLocation.find(sool => bord.name.toLowerCase().substring(0, sool.length) === sool) || bord.layer === 'markering ononderbroken')) {
                this.data.push({
                    text: `FOUND: ${bord.name}[${bord.handle}]`,
                    bord: bord,
                    firstLines: [bord],
                    connections: [[bord]]
                });
            } else {
                let filteredFirstConnections = searchForFirstConnectionsList.filter(entity => {
                    const coord = {
                        lat: (entity.lat ?? entity.wgsStart?.lat),
                        lng: (entity.lng ?? entity.wgsStart?.lng)
                    };
                    const coordEnd = entity?.wgsPoints?.length ? {
                        lat: (entity?.wgsPoints[entity?.wgsPoints?.length - 1]?.lat ?? entity.wgsEnd?.lat),
                        lng: (entity?.wgsPoints[entity?.wgsPoints?.length - 1]?.lng ?? entity.wgsEnd?.lng)
                    } : null;

                    if (coord.lng && (entity.points?.length < 11 || !entity.points)) {
                        return this.distanceM(bord, coord) < 2000 || (coordEnd && this.distanceM(bord, coordEnd) < 2000);
                    }

                    return false;
                }).filter(entity => {
                    if (!entity || this.excludedSearch.some(es => es.toLowerCase() === entity.layer.toLowerCase()) || !!this.excludedSearchIfContains.find(e => entity.layer.toLowerCase().includes(e))) {
                        return false;
                    }
                    if (['LWPOLYLINE', 'LINE'].includes(entity?.type)) {
                        if (entity.polygon && turf.booleanPointInPolygon(turf.point([bord.lng, bord.lat]), entity.polygon)) {
                            return true;
                        }
                        if (entity.polygon && bord.polygon) {
                            // bm21 is een zwart wit fietspaaltje
                            const bordPolygon = bord.entities.find(e => e.name === 'bm21')?.polygon ?? bord.polygon;
                            return turf.booleanIntersects(bordPolygon, entity.polygon);
                        }
                    }

                    return false;
                });
                if (!filteredFirstConnections?.length && bord.entities?.length) {
                    filteredFirstConnections = flattenedEntities.filter(entity => {
                        if (!entity || this.excludedSearch.indexOf(entity.layer.toLowerCase()) !== -1 || !!this.excludedSearchIfContains.find(e => entity.layer.includes(e))) {
                            return false;
                        }
                        if (['LWPOLYLINE', 'LINE'].includes(entity?.type)) {
                            let subFound = false;
                            bord.entities.filter(je => je.type === 'INSERT').forEach(subBord => {
                                if (subBord?.lat && entity.polygon && turf.booleanPointInPolygon(turf.point([subBord.lng, subBord.lat]), entity.polygon)) {
                                    subFound = true;
                                }
                            });
                            if (subFound) {
                                return true;
                            }
                        }

                        return false;
                    });
                }

                if (filteredFirstConnections?.length > 0) {
                    const connections = [];

                    const bordPoles = filteredFirstConnections.filter(f => {
                        if (f.type === 'LWPOLYLINE' && f.start[2] === 0.6 && f.start[3] === 0.6) {
                            const length = this.distanceM(f.wgsStart, f.wgsEnd);
                            return length > 2.2 && length < 10;
                        }
                        return false;
                    });
                    if (bordPoles?.length) {
                        filteredFirstConnections = bordPoles;
                    } else {
                        const heinLines = filteredFirstConnections.filter(f => {
                            return f.points?.filter(p => p[2] === 1.5 && p[3] === 1.5)?.length === 2;
                        })
                        if (heinLines.length) {
                            filteredFirstConnections = heinLines;
                        } else {
                            filteredFirstConnections = filteredFirstConnections.slice(0, 3);
                        }
                    }
                    // Only search for location in entities within 100m
                    const filteredFlattened = flattenedEntities.filter(flat => {

                        if (flat?.lat || flat.wgsStart?.lat) {
                            if (this.excludedSearch.includes(flat.layer.toLowerCase()) || !!this.excludedSearchIfContains.find(e => flat.layer.includes(e))) {
                                return false;
                            }
                            if (flat.parent_handle && !!bordenToSearch.find(b => b.handle === flat.parent_handle)) {
                                return false;
                            }
                            if (flat.points?.length > 10) {
                                return false;
                            }
                            return this.distanceM(bord, {
                                lat: (flat.lat ?? flat.wgsStart?.lat),
                                lng: (flat.lng ?? flat.wgsStart?.lng)
                            }) < 1000;
                        }

                        return false;
                    });
                    filteredFirstConnections.forEach(firstConnection => {

                        const groupedLines = [];
                        if (firstConnection.parent_handle) {
                            const parent = flattenedEntities.find(f => f.handle === firstConnection.parent_handle);
                            if (parent?.entities?.length) {
                                groupedLines.push(...parent.entities);
                            }
                        }

                        if (groupedLines?.length && groupedLines.filter(t => ['CIRCLE', 'HATCH'].includes(t.type) || t.layer === 'wy-kleur-010')?.length) {
                            connections.push(this.checkForConnections(firstConnection, bord, groupedLines, filteredFirstConnections));
                        } else if (firstConnection.points?.filter(p => p[2] === 1.5 && p[3] === 1.5)?.length === 2) {
                            connections.push([firstConnection]);
                        } else {
                            console.info('search all lines', bord.handle);
                            connections.push(this.checkForConnections(firstConnection, bord, filteredFlattened, filteredFirstConnections));
                        }
                    });
                    this.data.push({
                        text: `FOUND: ${bord.name}[${bord.handle}] start:[${filteredFirstConnections.map(f => f.handle).join('|')}]`,
                        bord: bord,
                        firstLines: filteredFirstConnections,
                        connections: connections
                    });
                } else {
                    const notFound = {
                        text: `NOT FOUND: ${bord.name}[${bord.handle}]`,
                        bord: bord,
                        connections: [[]],
                        otherPoints: bord.entities.filter(e => e.type === 'INSERT')
                    };
                    notFound.otherPoints.push(flattenedEntities.find(f => f?.handle === 84293554))
                    this.data.push(notFound);
                }
            }
        });
        console.info('search for locations');
        const borden: MapEntity[] = [];

        const findOnFarthestPoint = (foundBorden: {
            text: string;
            connections?: ImportEntity[][];
            firstLines?: ImportEntity[];
            bord?: ImportEntity;
            location?: ImportEntity;
            otherPoints?: ImportEntity[]
        }, hasLocation: MapEntity) => {
            let maxDistance = 0;
            let farthestPoint = null;
            let farthestPointObj = null as ImportEntity;
            let firstConnection = null;

            foundBorden.firstLines.forEach((firstLine, firstLineIndex) => {
                if (firstLine.parent_handle !== foundBorden.bord.handle) {
                    if (firstLine.wgsPoints) {
                        firstLine.wgsPoints.map(p => turf.point([p.lng, p.lat])).forEach(point => {
                            const distance = this.distanceCm(foundBorden.bord, point);
                            if (distance > maxDistance) {
                                maxDistance = distance;
                                farthestPoint = point;
                                farthestPointObj = firstLine;
                                firstConnection = firstLine;
                            }
                        });
                    } else {
                        const point = turf.point([firstLine.lng, firstLine.lat]);
                        const distance = this.distanceCm(foundBorden.bord, point);
                        if (distance > maxDistance) {
                            maxDistance = distance;
                            farthestPoint = point;
                            farthestPointObj = firstLine;
                            firstConnection = firstLine;
                        }
                    }
                    foundBorden.connections[firstLineIndex].forEach(c => {
                        if (c.parent_handle !== foundBorden.bord.handle) {
                            if (c.wgsPoints) {
                                c.wgsPoints.map(p => turf.point([p.lng, p.lat])).forEach(point => {
                                    const distance = this.distanceCm(foundBorden.bord, point);
                                    if (distance > maxDistance) {
                                        maxDistance = distance;
                                        farthestPoint = point;
                                        farthestPointObj = c;
                                        firstConnection = firstLine;
                                    }
                                });
                            } else {
                                const point = turf.point([c.lng, c.lat]);
                                const distance = this.distanceCm(foundBorden.bord, point);
                                if (distance > maxDistance) {
                                    farthestPoint = point;
                                    farthestPointObj = c;
                                    firstConnection = firstLine;
                                }
                            }
                        }
                    });
                }
            });

            if (farthestPoint) {
                const location = {
                    type: 'bord',
                    bord: foundBorden.bord,
                    location: farthestPointObj,
                    locations: [farthestPointObj],
                    firstConnection: firstConnection,
                    found: 'using farthest point'
                } as MapEntity;
                location.location.lat = farthestPoint.geometry.coordinates[1];
                location.location.lng = farthestPoint.geometry.coordinates[0];
                if (!hasLocation) {
                    const parent = flattenedEntities.find(f => f.handle === farthestPointObj?.parent_handle);
                    if (parent && this.endPointSpecificNames.includes(parent.name)) {
                        location.location = parent;
                    }
                    if (farthestPointObj.wgsPoints?.length > 2) {
                        location.connections = [farthestPointObj];
                    }
                    borden.push(location);
                    foundBorden.location = location.location;
                }
            }
        }


        this.data.forEach(foundBorden => {
            if (foundBorden.bord?.type === 'INSERT' && this.staysOnOwnLocation.find(sool => foundBorden.bord.name.substring(0, sool.length) === sool)) {
                borden.push({
                    type: 'bord',
                    bord: foundBorden.bord,
                    location: foundBorden.bord,
                    firstConnection: null,
                    connections: null
                });
            } else if (foundBorden.firstLines?.length === 1 && foundBorden.firstLines[0].points?.filter(p => p[2] === 1.5 && p[3] === 1.5)?.length === 2) { //hein
                // neem eerste lat/lng als locatie
                const locationCopy = JSON.parse(JSON.stringify(foundBorden.firstLines[0])) as ImportEntity;
                locationCopy.wgsPoints = locationCopy.wgsPoints.slice(0, 1);
                locationCopy.lng = locationCopy.wgsPoints[0].lng;
                locationCopy.lat = locationCopy.wgsPoints[0].lat;
                const location = {
                    type: 'bord',
                    bord: foundBorden.bord,
                    location: locationCopy,
                    locations: [locationCopy],
                    firstConnection: foundBorden.firstLines[0]
                } as MapEntity;
                borden.push(location);
                foundBorden.location = locationCopy;
            } else if (foundBorden.firstLines?.length) {
                // Sorteer firstlines op afstand tot locatie van bord. Soms komt het voor dat een andere dynamische lijn het bord net raakt aan bijvoorbeeld de bovenkant en dan als verkeerde lokatie gepakt wordt
                foundBorden.firstLines.sort((a, b) => {
                    const distA = Math.min(this.distanceM(a.wgsStart, foundBorden.bord), this.distanceM(a.wgsPoints ? a.wgsPoints[a.wgsPoints.length - 1] : a.wgsEnd, foundBorden.bord));
                    const distB = Math.min(this.distanceM(b.wgsStart, foundBorden.bord), this.distanceM(b.wgsPoints ? b.wgsPoints[b.wgsPoints.length - 1] : b.wgsEnd, foundBorden.bord));
                    return distA - distB;
                });
                const dynLineEntity = foundBorden.firstLines.find(f => !!f.parent_handle);
                let foundDynLocation = null;
                if (dynLineEntity?.parent_handle) {
                    const dynLine = this.flattened.find(f => f.handle === dynLineEntity.parent_handle);
                    const loc = dynLine?.entities?.filter(entity => {
                        return (['_HEK-dot-dyn', '_HEK_dot-dyn'].includes(dynLine.name) && entity.layer === 'wy-kleur-010');
                    });
                    if (loc?.length) {
                        const numberOfFences = foundBorden.bord.entities.filter(e => e.name?.toLowerCase()?.includes('afzh'))?.length;
                        const locations = numberOfFences > 1 ? loc.map(l => l.wgsPoints.map(l => [l.lng, l.lat])).flat() as any : loc;
                        foundDynLocation = {
                            type: 'bord',
                            bord: foundBorden.bord,
                            location: loc[0],
                            locations,
                            multiLocation: numberOfFences > 1 && loc?.length > 1,
                            firstConnection: dynLine.entities.find(entity => ['LWPOLYLINE', 'POLYLINE', 'LINE'].includes(entity.type) && entity.layer === '0'),
                            connections: dynLine.entities.filter(entity => ['LWPOLYLINE', 'POLYLINE', 'LINE'].includes(entity.type) && entity.layer === 'wx-zwart-007')
                        } as MapEntity;
                        foundDynLocation['locEnt'] = dynLine;
                        borden.push(foundDynLocation);
                        foundBorden.location = loc[0];
                    }
                }

                if (!foundDynLocation) {
                    foundBorden.firstLines.forEach((firstLine, index) => {
                        foundBorden.connections[index]?.forEach(connection => {
                            const hasLocation = borden.find(b => b.bord === foundBorden.bord); // bord heeft al een locatie van een andere connection / firstline
                            // Voldoet het item aan de voorwaarden voor een eindpunt
                            if ((this.endPointSpecificNames.includes(connection.name) || connection.name?.substring(0, 1) === '*' || ['_HEK-dot-dyn', '_HEK_dot-dyn', '_BRD-dot-dyn', '_BRD_dot-dyn'].includes(connection.name)
                                || (['CIRCLE', 'HATCH'].includes(connection.type) && connection.radius > 0.67 && connection.radius < 0.81)
                            ) && connection?.handle !== foundBorden.bord?.handle) {

                                const distanceBordAndConnection = this.distanceCm(foundBorden.bord, connection);
                                const distanceBordAndFirstline = Math.max(this.distanceCm(foundBorden.bord, firstLine.wgsStart ?? firstLine), this.distanceCm(foundBorden.bord, firstLine.wgsEnd ?? firstLine));
                                const diff = Math.abs(distanceBordAndConnection - distanceBordAndFirstline);
                                if (distanceBordAndConnection > 5 && diff > 5) {

                                    const location = {
                                        type: 'bord',
                                        bord: foundBorden.bord,
                                        location: connection,
                                        locations: [connection],
                                        firstConnection: firstLine,
                                        connections: foundBorden.connections[index]
                                    } as MapEntity;

                                    if (!hasLocation) {
                                        borden.push(location);
                                        foundBorden.location = connection;
                                    } else {
                                        if (!hasLocation.locations) {
                                            hasLocation.locations = [];
                                        }
                                        hasLocation.locations.push(connection);
                                    }

                                } else {
                                    findOnFarthestPoint(foundBorden, hasLocation);
                                }

                            }
                            if (connection.layer === 'wy-kleur-010' && connection.type === 'LWPOLYLINE' && connection?.wgsStart?.lat) {
                                const line = turf.lineString([
                                    [connection.wgsStart.lng, connection.wgsStart.lat],
                                    [connection.wgsEnd.lng, connection.wgsEnd.lat]
                                ]);
                                const lineLength = turf.length(line, {units: 'centimeters'});
                                if (lineLength > 180 && lineLength < 450) {
                                    const location = {
                                        type: 'bord',
                                        bord: foundBorden.bord,
                                        location: connection,
                                        locations: [connection],
                                        firstConnection: firstLine,
                                        connections: foundBorden.connections[index]
                                    } as MapEntity;

                                    if (!hasLocation) {
                                        borden.push(location);
                                        foundBorden.location = connection;
                                    } else {
                                        hasLocation.locations.push(connection);
                                    }
                                    if ((hasLocation?.location?.name?.substring(0, 1) === '*' ||
                                        hasLocation?.location?.name === '_HEK_dot-dyn' ||
                                        hasLocation?.location?.name === '_BRD-dot-dyn' ||
                                        this.endPointSpecificNames.includes(connection.name)) && hasLocation.location !== connection) {
                                        hasLocation.location = connection;

                                    }
                                }
                            }
                        });
                    });

                    // not found any connections?
                    const hasLocation = borden.find(b => b.bord === foundBorden.bord);
                    if (!hasLocation && foundBorden.firstLines?.length) {
                        const firstLine = foundBorden.firstLines[0];
                        // als firstline een verdikking heeft, pak eerste 2 coordinaten
                        if (firstLine.points?.length > 2 && firstLine?.points[0][2] >= 0.6 && firstLine.points[0][2] > 0.6) {
                            const locationCopy = JSON.parse(JSON.stringify(firstLine)) as ImportEntity;
                            locationCopy.wgsPoints = locationCopy.wgsPoints.slice(0, 2);
                            const location = {
                                type: 'bord',
                                bord: foundBorden.bord,
                                location: locationCopy,
                                locations: [locationCopy],
                                firstConnection: firstLine
                            } as MapEntity;

                            if (!hasLocation) {
                                borden.push(location);
                                foundBorden.location = locationCopy;
                            } else {
                                hasLocation.locations.push(locationCopy);
                            }
                        } else {
                            findOnFarthestPoint(foundBorden, hasLocation);
                        }

                    }
                }
            }
        });
        const sameLocationMap = new Map<number, MapEntity[]>();
        borden.filter(b => b.locations?.length > 1 && !!b.locations[0]?.handle).forEach(bord => {
            const withSameLocation = borden.filter(b => b.locations?.length > 1 && !!b.locations[0]?.handle).filter(b => b.location === bord.location);
            if (withSameLocation?.length > 1) {
                sameLocationMap.set(bord.location.handle, withSameLocation.sort((a, b) => {
                    return this.distanceCm(a.bord, a.location) - this.distanceCm(b.bord, b.location);
                }));
            }
        });
        Array.from(sameLocationMap.values()).forEach(bordenWsl => {
            const locationDistances = [];
            const locations = [];
            bordenWsl[0].locations.forEach(l => {
                const dist = Math.round(this.distanceCm(bordenWsl[0].bord, l));
                if (!locationDistances.includes(dist)) {
                    locationDistances.push(dist);
                    l['dist'] = dist;
                    locations.push(l);
                }
            });
            locations.sort((b, a) => {
                return this.distanceCm(bordenWsl[0].bord, a) - this.distanceCm(bordenWsl[0].bord, b);
            }).slice(0, bordenWsl.length).forEach((l, i) => {
                bordenWsl[i].location = l;
                bordenWsl[i].locationNr = i + 1;
            });
        });
        const otherItems = [];
        rawDataFromDxf.filter(e =>
            //filter verwijzingsnummers
            (!(e.space === 'paperspace' && this.referenceNr.includes(e.name) && !!e.attr['NR']) || e?.name === '_afwijking') &&
            //filter uitgezonderde delen
            !this.isInExcludedBoundary(e) &&
            //filter uitgezonderde lagen
            !this.excludedEntityLayers.includes(e.layer?.toLowerCase().trim()) &&
            (e.type !== 'INSERT' || (!!e.lat && !!e.lng))
        ).forEach(entity => {
            if (entity.type === 'INSERT' &&
                (!entity.space || entity.space === 'modelspace') &&
                !((entity.name?.toLowerCase()?.startsWith('*u') || entity.name?.toLowerCase()?.includes('dot-dyn')) && entity.entities.filter(e => ['CIRCLE', 'HATCH'].includes(e.type))?.length > 1 && entity.entities?.length > 4) &&
                !borden.find(b => b.bord === entity) &&
                !borden.find(b => b.firstConnection === entity) &&
                !borden.find(b => b.firstConnection?.parent_handle === entity.handle) &&
                !borden.find(b => b.location === entity) &&
                !borden.find(b => b.locations?.find(l => l.handle === entity.handle || l.parent_handle === entity.handle)) &&
                !borden.find(b => b.location?.handle === entity.handle) &&
                !borden.find(b => b.location?.parent_handle === entity.handle) &&
                // het komt helaas soms voor dat de lijnen van een dynamische lijn op de juiste lagen staan maar de insert op een uitgesloten laag
                (!this.excludedSearch.includes(entity.layer) || this.dynLocationNames.includes(entity.name) || entity.layer.includes('markering'))) {
                borden.push({
                    type: 'bord',
                    bord: entity,
                    location: entity,
                    firstConnection: null
                } as MapEntity);
            }
            if (entity.type === 'LWPOLYLINE' && entity.layer.startsWith('geluidsscherm')) {
                entity.name = 'geluidsscherm';
                otherItems.push({
                    type: 'geluidsscherm',
                    bord: entity,
                    location: null,
                    firstConnection: null
                } as MapEntity);
            }

            const markeringStartsWith = ['markering', 'demarkeren', 'mk-'];
            if (entity.type === 'LWPOLYLINE' && markeringStartsWith.some(msw => entity.layer.startsWith(msw))) {
                entity.name = entity.layer.startsWith('demarkeren') ? 'demarkeren' : 'markering';
                let pattern = entity.pattern;
                if (pattern && entity.linetype !== 'Continuous') {
                    if (entity.pattern.slice(0, 1) === entity.pattern.slice(-1)) {
                        pattern = pattern.slice(0, -1);
                    }
                    const fill = pattern?.replaceAll(' ', '')?.length;
                    const empty = pattern?.replaceAll('_', '')?.length;
                    entity.attr = {
                        pattern,
                        linetype: entity.linetype,
                        fill,
                        empty
                    };
                }
                if (!entity.attr) {
                    entity.attr = {
                        linetype: 'markering ononderbroken'
                    };
                }
                borden.push({
                    type: 'markering',
                    bord: entity,
                    location: null,
                    firstConnection: null
                } as MapEntity);
            }
            if (entity.type === 'HATCH' && markeringStartsWith.some(msw => entity.layer.startsWith(msw))) {
                entity.name = entity.layer.startsWith('demarkeren') ? 'demarkeren-vlak' : 'markering-vlak';
                entity.attr = {
                    linetype: 'vlak'
                };
                borden.push({
                    type: 'markering-vlak',
                    bord: entity,
                    location: null,
                    firstConnection: null
                } as MapEntity);
            }
            if (entity.type === 'LWPOLYLINE' && entity.layer === 'werkvak') {
                entity.name = 'werkvak';
                otherItems.push({
                    type: 'werkvak',
                    bord: entity,
                    location: null,
                    firstConnection: null
                } as MapEntity);
            }
            if (entity.type === 'LWPOLYLINE' && entity.layer === 'tijdelijke verharding') {
                entity.name = 'tijdelijke verharding';
                otherItems.push({
                    type: 'tijdelijke_verharding',
                    bord: entity,
                    location: null,
                    firstConnection: null
                } as MapEntity);
            }
            if (entity.type === 'LWPOLYLINE' && entity.layer.startsWith('barrier')) {
                entity.name = 'barrier';
                otherItems.push({
                    type: 'barrier',
                    bord: entity,
                    location: null,
                    firstConnection: null
                } as MapEntity);
            }
            if (entity.type === 'LWPOLYLINE' && entity.layer === 'hek (bouwhek)') {
                entity.name = 'bouwhek';
                otherItems.push({
                    type: 'hek',
                    bord: entity,
                    location: null,
                    firstConnection: null
                } as MapEntity);
            }
            if (['MTEXT', 'TEXT'].includes(entity.type)) {
                entity.name = 'text';
                entity.attr = {text: entity.text};
                const otherText = {
                    type: 'bord',
                    bord: entity,
                    location: entity,
                    firstConnection: null
                } as MapEntity;
                this.getLineByText(otherText);
                otherItems.push(otherText);
            }
            if (['LEADER'].includes(entity.type)) {

                entity.name = 'arrow';
                otherItems.push({
                    type: 'arrow',
                    bord: entity,
                    location: null,
                    firstConnection: null
                } as MapEntity);
            }
        });

        console.info(`found locations: ${this.data.filter(d => !!d.location)?.length}`);

        const prioMap = new Map<TtmTypes, number>();
        ttmTypes.forEach(ttmType => {
            prioMap.set(ttmType.key, ttmType.priority);
        });

        borden.forEach(bord => {
            if (bord.connections?.filter(c => !!c)?.length || (bord.firstConnection && bord.location)) {
                if (bord.connections?.length === 1 && this.endPointSpecificNames.includes(bord.connections[0].name) && !bord.firstConnection?.parent_handle) {
                    bord.lineToLocation = turf.lineString(bord.firstConnection.wgsPoints.map(f => [f.lng, f.lat]));
                } else if (bord.location.parent_handle) {
                    bord.lineToLocation = this.lineFromGroupedLines(bord, flattenedEntities);
                } else if (bord.location && bord.firstConnection.points?.filter(p => p[2] === 1.5 && p[3] === 1.5)?.length === 2) {
                    bord.lineToLocation = turf.lineString(bord.firstConnection.wgsPoints.map(f => [f.lng, f.lat]));
                } else if (bord.location && bord.firstConnection?.wgsPoints?.length > 2 && !bord.connections?.filter(c => !!c?.handle && c.layer !== 'wy-kleur-010' && bord.firstConnection.handle !== c?.handle)?.length) {
                    bord.lineToLocation = turf.lineString(bord.firstConnection.wgsPoints.map(f => [f.lng, f.lat]));
                } else if (bord.location && bord.firstConnection?.entities?.length > 2 && !bord.connections?.filter(c => c.parent_handle !== bord.location.handle && c.handle !== bord.location.handle && c.layer !== 'wy-kleur-010')?.length) {
                    bord.lineToLocation = turf.lineString(bord.firstConnection.wgsPoints.map(f => [f.lng, f.lat]));
                } else {
                    const connections = [bord.firstConnection, ...(bord.connections || [])];
                    if (['LWPOLYLINE', 'LINE'].includes(bord.location.type)) {
                        connections.push(bord.location);
                    }
                    bord.lineToLocation = this.extractLineFromConnections(connections, bord.bord, bord.location, bord.firstConnection);
                }
                if (!bord.lineToLocation && bord.location) {
                    const locationPoint = [bord.location?.lng ?? bord.location?.wgsStart?.lng, bord.location?.lat ?? bord.location?.wgsStart?.lat];
                    const bordPoint = [bord.bord.lng, bord.bord.lat];
                    bord.lineToLocation = turf.lineString([locationPoint, bordPoint])
                }
            }
            let finalType = this.detectType(bord.bord);
            let prio = prioMap.get(finalType);
            bord.bord?.entities?.filter(e => e.type === 'INSERT').forEach(b => {
                const bordType = this.detectType(b);
                if (prioMap.get(bordType) < prio) {
                    finalType = bordType;
                    prio = prioMap.get(bordType);
                }
            });
            bord.ttmType = finalType;
        });
        borden.forEach(bord => {
            bord.hasEntities = [];
            let belongsTogether = [];
            do {

                belongsTogether = this.beloningTogether(bord, borden);
                bord.hasEntities.push(...belongsTogether);
            } while (belongsTogether.length > 0);
            bord.hasEntities.forEach(hasEntity => {
                hasEntity.belongsToEntity = bord;
            });
        });
        const aavMaatregelen = borden.filter(e => e.bord?.name?.includes('afplak-afdraai-verwijder'));
        aavMaatregelen.forEach(aav => {
            const bufferedPoly = this.makeConvexOfEntity(aav);
            borden.filter(b => b.type === 'bord' && b.ttmType === TtmTypes.permanent_signs && !b.bord?.name.includes('afplak-afdraai-verwijder'))
                .forEach(otherBord => {
                    const point = turf.point([otherBord.bord.lng, otherBord.bord.lat]);
                    if (turf.booleanPointInPolygon(point, bufferedPoly) || turf.pointToPolygonDistance(point, bufferedPoly, {units: 'meters'}) < 0.2) {
                        aav.belongsToEntity = otherBord;
                        if (aav.bord.config === 'afplakken') {
                            aav.ttmStatus = 'to_be_taped';
                            otherBord.ttmStatus = 'to_be_taped';
                        } else {
                            aav.ttmStatus = 'temporarily_placed';
                            otherBord.ttmStatus = 'temporarily_placed';
                        }
                    }
                });
        });

        borden.filter(b => !!b.bord.name).forEach(bord => {
            bord.bord.rotation = this.getBordBearing(bord.bord, bord);
        });

        const bundleRows = [
            {regex: new RegExp(/^schild(-*)([0-9]|as)$/i), group: 'schild'},
            {regex: new RegExp(/^keg[0-9]$/i), group: 'keg'}
        ];
        bundleRows.forEach(bundle => {
            const groupedBorden = this.bundleCones(borden.filter(b => !!b.bord.name), bundle.regex);
            groupedBorden.forEach((gb, index) => {
                gb.forEach(b => {
                    b.group = `${bundle.group}-${index}`;
                });
            });
        });


        // icon/nummer circles op kaart koppelen aan bord in legenda

        const numberCircleWithoutRotation = allFlattenedEntities
            .filter(b => !!b.lat && b.rotation === 0 && this.referenceNr.includes(b.name) && !!b.attr['NR'])
            .filter((b, i, currentArray) => currentArray.filter(nn => nn.name === b.name)?.length === 1 || (+`${b.attr['NR']}`.replace('V', '')) > 0)
            .filter((nwr, i, arr) => {
                if (arr.filter(a => a.name === nwr.name && a.attr['NR'] === nwr.attr['NR'] && a.space !== nwr.space).length > 0) {
                    return nwr.space === 'paperspace';
                }
                return true;
            })
            .sort((a, b) => {
                return +a.attr['NR'] - +b.attr['NR'];
            });
        const numberBordCombination = [];

        numberCircleWithoutRotation.forEach(numberBord => {
            const numberPoint = turf.point([numberBord.lng, numberBord.lat]);
            const nearestBord = {
                distance: 9999,
                bord: null
            };
            allFlattenedEntities
                .filter(e => (
                        numberBord.name !== '_afwijking' && e.rotation === 0 && !!e.name &&
                        this.allowedTypes.find(at => e.name.toLowerCase().startsWith(at.toLowerCase()))
                    ) || (
                        numberBord.name === '_afwijking' && e?.type === 'MTEXT'
                    )
                )
                .filter(b => !this.referenceNr.includes(b.name) && !!b.polygon && !numberBordCombination.map(nbc => nbc.bord.handle).includes(b.handle))
                .forEach(b => {

                    let distance = (b?.type === 'MTEXT' && b.block_min) ? (Math.abs(b.block_min[0] - numberBord.block_min[0]) + Math.abs(b.block_min[1] - numberBord.block_min[1])) : turf.pointToPolygonDistance(numberPoint, b.polygon, {units: 'meters'});
                    if (numberBord.name === '_nr' && b.name.startsWith('Bordvooraank') && nearestBord.bord && !nearestBord?.bord['name'].startsWith('Bordvooraank')) {
                        distance = nearestBord.distance - 1;
                    }

                    if (nearestBord.distance > distance && !numberBordCombination.map(nbc => nbc.bord.handle).includes(b.parent_handle)) {
                        nearestBord.distance = distance;
                        nearestBord.bord = b;
                        if (b.parent_handle) {
                            nearestBord.bord = allFlattenedEntities.find(a => a.handle === b.parent_handle);
                        }
                    }
                });
            if (nearestBord.bord) {

                numberBordCombination.push({
                    number: numberBord,
                    bord: nearestBord.bord
                });
            }
        });

        // soms is een vooraankondiging op de kaart een IcoVooraank en in de legenda op de paperspace een _nr
        const replaceWithMap = new Map<string, string>([
            ['IcoVooraank', '_nr']
        ]);
        numberBordCombination.forEach(nbc => {
            const instances = borden.filter(b =>
                !numberCircleWithoutRotation.map(n => n.handle).includes(b.bord.handle) &&
                this.referenceNr.includes(b.bord.name) &&
                !!b.bord.attr['NR'] &&
                (replaceWithMap.get(b.bord.name) ?? b.bord.name) === nbc.number.name &&
                b.bord.attr['NR'] === nbc.number.attr['NR']
            );
            instances.forEach(instance => {
                instance.location = instance.bord;
                instance.bord = nbc.bord;
                instance.bord.lat = instance.location.lat;
                instance.bord.lng = instance.location.lng;
                instance.bord.block_min_lat = null;
                instance.bord.block_min_lng = null;
                instance.bord.block_max_lat = null;
                instance.bord.block_max_lng = null;
            });
        });


        this.bordenForExport = borden
            .filter(b => !b.bord?.name?.includes('afplak-afdraai-verwijder') || !!b.belongsToEntity)
            .sort((a, b) => {
                return b.hasEntities?.length - a.hasEntities?.length;
            });
        this.otherItemsForExport = otherItems;
        return true;
    }

    getLineByText(textItem: MapEntity) {

        if (!textItem.bord.polygon) {
            return null;
        }
        const nearLines = this.flattened.filter(f => ['LINE', 'LWPOLYLINE'].includes(f.type)).filter(entity => {
            const coord = {
                lat: (entity.lat ?? entity.wgsStart?.lat),
                lng: (entity.lng ?? entity.wgsStart?.lng)
            };
            const coordEnd = entity?.wgsPoints?.length ? {
                lat: (entity?.wgsPoints[entity?.wgsPoints?.length - 1]?.lat ?? entity.wgsEnd?.lat),
                lng: (entity?.wgsPoints[entity?.wgsPoints?.length - 1]?.lng ?? entity.wgsEnd?.lng)
            } : null;

            if (textItem.bord?.lat && coord.lng && (entity.points?.length < 11 || !entity.points)) {
                return this.distanceM(textItem.bord, coord) < 500 || (coordEnd && this.distanceM(textItem.bord, coordEnd) < 500);
            }

            return false;
        });


        nearLines.forEach(nearLine => {
            if (nearLine.polygon && turf.booleanIntersects(nearLine.polygon, textItem.bord.polygon)) {
                textItem.lineToLocation = turf.lineString(nearLine.wgsPoints.map(f => [f.lng, f.lat]));
                textItem.bord.color = nearLine.color;
            }
        });

    }

    bundleCones(borden: MapEntity[], toBundle: RegExp): MapEntity[][] {
        const groupedBorden = [] as { bord: MapEntity, lat?: number, lng?: number, matchWith?: any }[][];
        const rotationToleranceMapByTtmType = new Map<TtmTypes, {
            maxDistanceInM: number;
            maxRotationDifference: number
        }[]>([
            [
                TtmTypes.beacons_cones,
                [
                    {maxDistanceInM: 13, maxRotationDifference: 50},
                    {maxDistanceInM: 20, maxRotationDifference: 30},
                    {maxDistanceInM: 30, maxRotationDifference: 20},
                    {maxDistanceInM: 40, maxRotationDifference: 10},
                    {maxDistanceInM: 51, maxRotationDifference: 2}
                ]
            ],
            [
                TtmTypes.mobile_light_poles,
                [
                    {maxDistanceInM: 18, maxRotationDifference: 50},
                    {maxDistanceInM: 30, maxRotationDifference: 30},
                    {maxDistanceInM: 75, maxRotationDifference: 10},
                    {maxDistanceInM: 80, maxRotationDifference: 2}
                ]
            ]
        ]);

        const groupedHandles = [];
        const toBundleBorden = borden.filter(b => b.bord.name.match(toBundle));
        toBundleBorden.forEach((currentBord, i, filteredBorden) => {
            filteredBorden.filter(ob => this.distanceM(ob.bord, currentBord.bord) < 26 && ob.bord.handle !== currentBord.bord.handle && currentBord.bord.name === ob.bord.name && (Math.abs(Math.abs(currentBord.bord.xScale) - Math.abs(ob.bord.xScale)) < 0.05))
                .forEach(otherBord => {
                    const distance = this.compareWithBearing(otherBord.bord, currentBord.bord);
                    // haal het toegestane rotatie verschil op uit de map
                    const applicableTolerance = rotationToleranceMapByTtmType.get(currentBord.ttmType).find(rt => distance?.distanceBetween <= rt.maxDistanceInM)
                        ?.maxRotationDifference;
                    // controleer of de afstand tot de gemiddelde rotatie lijn binnen de marge is en of de rotatie niet meer is als toegstaan. Daarnaast mag de afstand tussen de middenlijn en van insert tot insert niet meer als de helft zijn
                    if (distance && (distance.distanceBearingMid < Math.max(distance.distanceBetween * (distance.bearingDiff * 0.1), 0.5) && distance.bearingDiff < applicableTolerance) && distance.distanceBearingMid < (distance.distanceBetween * 0.5)) {
                        let index = groupedBorden.findIndex(g => g.find(gb => currentBord.bord.handle === gb.bord.bord.handle || otherBord.bord.handle === gb.bord.bord.handle));

                        // factor wat de gemiddelde afstand af mag wijken tussen bakens
                        const maxAvgDistFactor = 1.75;
                        //check average distance between bord and other bord
                        if (index < 0 || distance.distanceBetween < (groupedBorden[index].map(gb => gb['distanceBetween']).reduce((sum, currentValue) => sum + currentValue, 0) / groupedBorden[index].length) * maxAvgDistFactor) {
                            if (index < 0) {
                                const group = [{
                                    ...distance,
                                    lat: currentBord.bord.lat,
                                    lng: currentBord.bord.lng,
                                    bord: currentBord,
                                    matchWith: otherBord
                                }, {
                                    ...distance,
                                    lat: otherBord.bord.lat,
                                    lng: otherBord.bord.lng,
                                    bord: otherBord,
                                    matchWith: currentBord
                                }];
                                groupedBorden.push(group);
                            } else {
                                if (!groupedBorden[index].find(gb => gb.bord.bord.handle === currentBord.bord.handle)) {
                                    groupedBorden[index].push({
                                        ...distance,
                                        lat: currentBord.bord.lat,
                                        lng: currentBord.bord.lng,
                                        bord: currentBord,
                                        matchWith: otherBord
                                    });
                                }
                                if (!groupedBorden[index].find(gb => gb.bord.bord.handle === otherBord.bord.handle)) {
                                    groupedBorden[index].push({
                                        ...distance,
                                        lat: otherBord.bord.lat,
                                        lng: otherBord.bord.lng,
                                        bord: otherBord,
                                        matchWith: currentBord
                                    });
                                }
                            }
                            groupedHandles.push(currentBord.bord.handle);
                            groupedHandles.push(otherBord.bord.handle);
                            otherBord.bord['pos'] = distance;
                        }
                    }
                });
        });

        //bundel de overgebleven punten met clustersDbscan
        const points = [];
        toBundleBorden.filter(b => !groupedHandles.includes(b.bord.handle))
            .forEach(b => {
                const point = turf.point([b.bord.lat, b.bord.lng], {
                    handle: b.bord.handle
                });
                points.push(point);
            });

        const clusters = turf.clustersDbscan(turf.featureCollection(points), 25, {units: 'meters'});
        const clusterNumbers = Array.from(new Set(clusters.features.map(p => p.properties.cluster)));
        clusterNumbers.forEach(clusterNumber => {
            const includedItems = clusters.features.filter(p => p.properties.cluster === clusterNumber && ['core', 'edge'].includes(p.properties.dbscan)).map(p => p.properties['handle']);
            groupedBorden.push(toBundleBorden.filter(b => includedItems.includes(b.bord.handle)).map(p => {
                return {
                    bord: p
                }
            }));
        });

        return groupedBorden.map(gb => gb.map(b => b.bord));
    }

    createDirectionLine(lat, lng, bearing, lengthMeters = 100) {
        const point = turf.point([lng, lat]);
        const destination = turf.destination(point, lengthMeters, bearing, {units: 'meters'});
        return turf.lineString([point.geometry.coordinates, destination.geometry.coordinates]);
    }

    // Functie die de rotatie van zowel bord A als B berekent voor een keg of schild
    getBordBearing(bord: ImportEntity, mapEntity?: MapEntity) {
        if (bord.name.startsWith('keg')) {
            const bordBakenLine = bord.entities.find(e => e.layer === 'wx-zwart-007');
            if (!bordBakenLine) {
                return null;
            }
            const kegLine = bord.entities.find(e => e.layer === 'wx-zwart-008');
            const kegLineBottom = turf.lineString([
                [bordBakenLine.wgsPoints[0].lat, bordBakenLine.wgsPoints[0].lng],
                [bordBakenLine.wgsPoints[1].lat, bordBakenLine.wgsPoints[1].lng]
            ]);
            const kegLineTop = turf.lineString([
                [kegLine.wgsPoints[1].lat, kegLine.wgsPoints[1].lng],
                [kegLine.wgsPoints[2].lat, kegLine.wgsPoints[2].lng]
            ]);
            const lengthTop = turf.length(kegLineTop, {units: 'meters'});
            const lengthBottom = turf.length(kegLineBottom, {units: 'meters'});
            const lengthToCreatePoint = (lengthBottom / 2) - (lengthTop / 2);
            const topLineBearing = turf.bearing([kegLine.wgsPoints[1].lat, kegLine.wgsPoints[1].lng], [kegLine.wgsPoints[2].lat, kegLine.wgsPoints[2].lng]);
            const destination = turf.destination(turf.point([kegLine.wgsPoints[2].lat, kegLine.wgsPoints[2].lng]), lengthToCreatePoint, topLineBearing, {units: 'meters'})
            return turf.bearing(turf.point([bord.lat, bord.lng]), destination);
        } else if (bord.name.startsWith('schild')) {
            const bordBakenLine = bord.entities.find(e => e.layer === 'wx-zwart-008' && e.type === 'LWPOLYLINE');
            if (!bordBakenLine) {
                return null;
            }
            const bordLine = turf.lineString([
                [bordBakenLine.wgsPoints[1].lat, bordBakenLine.wgsPoints[1].lng],
                [bordBakenLine.wgsPoints[2].lat, bordBakenLine.wgsPoints[2].lng]
            ]);
            return turf.bearing(bordLine.geometry.coordinates[0], bordLine.geometry.coordinates[1]);
        } else if (bord.name.includes('_HEK-los-dyn')) {
            const lines = [];
            bord.entities.flatMap(e => {
                return e.entities;
            }).filter(e => e?.layer === 'wx-zwart-007' && e.type === 'LINE').forEach(e => {
                if (!e.points && e.start) {
                    e.points = [e.start, e.end];
                }
                e.wgsPoints = e.points?.map(p => {
                    const wgsCoordinates = rdToWgs84(p[0] / 1000, p[1] / 1000);
                    return {
                        lat: wgsCoordinates.lat,
                        lng: wgsCoordinates.lon
                    };
                });

                if (turf.length(turf.lineString(e.wgsPoints.map(f => [f.lng, f.lat])), {units: 'meters'}) > 1) {
                    lines.push(e);
                }
            });
            if (lines?.length && lines[0].wgsPoints) {
                return turf.bearing(
                    [lines[0].wgsPoints[1].lat, lines[0].wgsPoints[1].lng],
                    [lines[0].wgsPoints[0].lat, lines[0].wgsPoints[0].lng]
                );
            }
        } else if (
            mapEntity &&
            this.hasDynLocation(mapEntity) &&
            !bord.name.toLowerCase().includes('hek') &&
            mapEntity.firstConnection.type === 'LWPOLYLINE'
        ) {
            const dist0 = this.distanceM(mapEntity.firstConnection.wgsPoints[0], bord);
            const dist1 = this.distanceM(mapEntity.firstConnection.wgsPoints[1], bord);
            const firstIs1 = dist0 < dist1;
            return turf.bearing(
                [mapEntity.firstConnection.wgsPoints[firstIs1 ? 1 : 0].lat, mapEntity.firstConnection.wgsPoints[firstIs1 ? 1 : 0].lng],
                [mapEntity.firstConnection.wgsPoints[firstIs1 ? 0 : 1].lat, mapEntity.firstConnection.wgsPoints[firstIs1 ? 0 : 1].lng]
            );
        }
        // todo borden zonder paal, hein borden
        return null;
    };

    compareWithBearing(bordA: ImportEntity, bordB: ImportEntity) {
        const bordBearingA = bordA.rotation;
        const bordBearingB = bordB.rotation;
        if (!bordBearingA || !bordBearingB) {
            return null;
        }
        const bordBearingMid = (bordBearingA + bordBearingB) / 2;// Gemiddelde van de rotatie van bord A en B
        let bordBearingDiff = Math.abs(bordBearingA - bordBearingB) % 360; // Verschil in rotatie van bord A en B gecorrigeerd voor -180 tot 180 graden
        if (bordBearingDiff > 180) bordBearingDiff = 360 - bordBearingDiff;
        const bordPointA = turf.point([bordA.lat, bordA.lng]);
        const bordPointB = turf.point([bordB.lat, bordB.lng]);

        // Maak rotatie lijnen vanaf bord B en bereken de afstand van bord A tot deze lijn
        const bordBRotationLine = this.createDirectionLine(bordB.lng, bordB.lat, bordBearingB);
        const distanceBtoAline = turf.pointToLineDistance(bordPointA, bordBRotationLine, {units: 'meters'});
        const bordAMidBordBRotationLine = this.createDirectionLine(bordB.lng, bordB.lat, bordBearingMid);
        const distanceMidBtoAline = turf.pointToLineDistance(bordPointA, bordAMidBordBRotationLine, {units: 'meters'});
        const distanceAtoB = turf.distance(bordPointA, bordPointB, {units: 'meters'}); // afstand in een rechte lijn tussen bord A en B

        bordB['rotLine'] = bordBRotationLine;
        bordB['rotLine2'] = bordAMidBordBRotationLine;
        return {
            distanceBearing: distanceBtoAline,
            distanceBearingMid: distanceMidBtoAline,
            distanceBetween: distanceAtoB,
            bearingDiff: bordBearingDiff
        }
    }

    entityToPoints(entities: ImportEntity[]) {
        const points = [];
        const entityToPoints = (entities: ImportEntity[], xScale = 1, yScale = 1) => {
            entities.forEach(ent => {
                if (['LWPOLYLINE', 'LINE', 'SOLID'].includes(ent.type)) {
                    const pp = ent.points ?? [ent.start, ent.end];
                    pp.forEach(p => {

                        if (p && p[0] > 1000) {
                            const z = rdToWgs84(p[0] * xScale, p[1] * yScale);
                            if (!z.error && z.lat && z.lon) {
                                points.push(turf.point([z.lon, z.lat], {handle: ent.handle}));
                            }
                        }
                    });
                }
                if (ent.entities?.length) {
                    entityToPoints(ent.entities);
                }
            });
        };
        entityToPoints(entities);
        return points;
    };

    makeConvexOfEntity(bord: MapEntity) {

        const points = [];
        points.push(...this.entityToPoints(bord.bord.entities));
        if (bord.hasEntities?.length) {
            bord.hasEntities.forEach((entity) => {
                points.push(...this.entityToPoints(entity.bord.entities));
            });
        }
        bord.bord['outlinePoints'] = points;
        const poly = turf.convex(turf.featureCollection(points));
        if (poly) {
            bord.bord['outlinePoly'] = poly;
            return turf.buffer(poly, 25, {units: 'centimeters'});
        }

        return null;
    }

    hasDynLocation(bord: MapEntity) {
        if (bord.location?.parent_handle) {
            const locationParent = this.flattened.find(f => f.handle === bord.location.parent_handle);
            if (locationParent) {
                return this.endPointSpecificNames.includes(locationParent.name) || locationParent.name?.substring(0, 1) === '*' || this.dynLocationNames.includes(locationParent.name);
            }
            return false;
        }
        return false;
    }

    beloningTogether(bord: MapEntity, borden: MapEntity[]): MapEntity[] {
        const belongsToThis = [];
        if (bord.type === 'bord' && [TtmTypes.closures, TtmTypes.rolling_equipment, TtmTypes.permanent_signs, TtmTypes.matrix_signs].includes(bord.ttmType)) {

            const bufferedPoly = this.makeConvexOfEntity(bord);
            if (bufferedPoly) {
                borden
                    .filter(otherBord => ![TtmTypes.permanent_signs, TtmTypes.beacons_cones, TtmTypes.mobile_light_poles].includes(otherBord.ttmType))
                    .filter(otherBord => !this.hasDynLocation(otherBord) && !otherBord.bord?.attr?.NR && otherBord.bord?.lat && !bord.hasEntities.find(he => he.bord.handle === otherBord.bord.handle))
                    .forEach(otherBord => {
                        const point = turf.point([otherBord.bord.lng, otherBord.bord.lat]);
                        const maxRotationDiff = 10;
                        const rotationDifference = Math.min(
                            Math.abs(bord.bord.rotation - otherBord.bord.rotation),
                            360 - Math.abs(bord.bord.rotation - otherBord.bord.rotation)
                        );
                        const bboxParallel = this.parallel(bord, otherBord);

                        if (bord !== otherBord && (bboxParallel || rotationDifference <= maxRotationDiff)) {
                            if (turf.booleanPointInPolygon(point, bufferedPoly) || turf.pointToPolygonDistance(point, bufferedPoly, {units: 'meters'}) < 2) {
                                belongsToThis.push(otherBord);
                            }
                            const typesOnDistanceAndRotation = [TtmTypes.closures, TtmTypes.detour_signs];

                            if (typesOnDistanceAndRotation.includes(bord.ttmType) && typesOnDistanceAndRotation.includes(otherBord.ttmType)) {
                                const distance = this.distanceM(otherBord.bord, bord.bord);
                                if (distance < 40 && bord.bord.polygon && otherBord.bord.polygon) {
                                    const bbox1 = turf.bbox(bord.bord.polygon);
                                    const bbox2 = turf.bbox(otherBord.bord.polygon);
                                    const tolerance = 1;
                                    const horizontallyAligned = bbox1[3] >= bbox2[1] - tolerance && bbox1[1] <= bbox2[3] + tolerance && bbox1[2] < bbox2[0] + tolerance;
                                    const verticallyAligned = bbox1[2] >= bbox2[0] - tolerance && bbox1[0] <= bbox2[2] + tolerance && bbox1[3] < bbox2[1] + tolerance;
                                    const distanceLocation = this.distanceM(otherBord.location, bord.location);
                                    const maxDistance = bord.ttmType === otherBord.ttmType ? 3.2 : 8;
                                    if ((distanceLocation < maxDistance || distance < maxDistance) && (horizontallyAligned || verticallyAligned)) {
                                        belongsToThis.push(otherBord);
                                    }
                                }

                            }
                        }
                    });
            }
        }

        return belongsToThis;
    }

    // bord types
    detectType(bord: ImportEntity): TtmTypes {
        const typeRegMap = new Map<RegExp, TtmTypes>([
            [new RegExp(/^RVVE0[0-3]$/i), TtmTypes.parking_bans],
            [new RegExp(/^RVV([JFCAGEBDLK][0-9abRLze\-]{1,7})(_[JFCAGEBDLK][0-9abRLze\-]{0,8})*$/i), TtmTypes.signs],
            [new RegExp(/^_inut[-a-z]*$/i), TtmTypes.signs],
            [new RegExp(/^_(afzh|wiu|)([0-9]{2,3})$/i), TtmTypes.closures],
            [new RegExp(/^_andreas[-a-z]*$/i), TtmTypes.andreas],
            [new RegExp(/^RVV[L][0-9\-JFCAGEBDLK]{1,14}t$/i), TtmTypes.detour_signs],
            [new RegExp(/^RVV[T][0-9\-JFCAGEBDLKR]{1,14}[RL]*$/i), TtmTypes.detour_signs],
            [new RegExp(/^rbra|rbrd|rbla|rblae|rbrae|rbrde$/i), TtmTypes.route_signs],
            [new RegExp(/^IcoVooraank$/i), TtmTypes.announcements],
            [new RegExp(/^b0rd_oml[-a-z_]*$/i), TtmTypes.detour_signs],
            [new RegExp(/^b0rd_vlg[-a-z_]*$/i), TtmTypes.detour_signs],
            [new RegExp(/^(b0rd|borto)([0-9]{0,3})$/i), TtmTypes.detour_signs],
            [new RegExp(/^schild(-*)([0-9]|as)$/i), TtmTypes.beacons_cones],
            [new RegExp(/^keg[0-9]$/i), TtmTypes.beacons_cones],
            [new RegExp(/^_cube$|^_lichtm$/i), TtmTypes.mobile_light_poles],
            [new RegExp(/^[_]aktiew|tekstw|splitsk|_verdrk|MRS-2DYNAMISCH|raamws|PORT[0-9]MAT$/i), TtmTypes.rolling_equipment],
            [new RegExp(/^matrix_km|MATR|MATKRU$/i), TtmTypes.rolling_equipment],
            [new RegExp(/^MATR|MATKRU$/i), TtmTypes.rolling_equipment],
            [new RegExp(/^[a-z_\-0-9]*raamwrk$/i), TtmTypes.signs],
            [new RegExp(/^best|aanwezige-bebording/i), TtmTypes.permanent_signs],
            [new RegExp(/^[0-9]MAT_DYN|portdynamisch/i), TtmTypes.matrix_signs],
            [new RegExp(/^bord-sbb.*|.*bord_sbb_.*/i), TtmTypes.shipping_measures],
            [new RegExp(/^_afwijking/i), TtmTypes.deviations],
            [new RegExp(/^_tekstw/i), TtmTypes.rolling_messages],
            [new RegExp(/^vkr.*/i), TtmTypes.traffic_controllers],
            [new RegExp(/^vri.*|drukknop/i), TtmTypes.temporary_vri],
            [new RegExp(/^afplakken|_nr_a/i), TtmTypes.tape_off],
            [new RegExp(/^markering.*/i), TtmTypes.temporary_road_markings],
            [new RegExp(/^_nr|Bordvooraank.*/i), TtmTypes.announcements],
            [new RegExp(/^_nr_p.*/i), TtmTypes.parking_bans]
        ]);
        let assignedType = TtmTypes.other;
        typeRegMap.forEach((type, regex) => {
            if (bord.name?.match(regex)) {
                assignedType = type;
            }
        });

        if (!!bord.attr && bord.attr['OMLEIDING']) {
            assignedType = TtmTypes.detour_signs;
        }
        if (bord.layer.includes('markering')) {
            assignedType = TtmTypes.temporary_road_markings;
        }

        return assignedType;
    }

    checkForConnections(inpEntity: ImportEntity, bord: ImportEntity, data: ImportEntity[], firstLines: ImportEntity[]) {
        const allFoundConnections = [];
        const findConnections = (searchForEntity, prevConnection: ImportEntity) => {
            return data.filter(someEntity => {

                if (this.excludedSearch.some(es => es.toLowerCase() === someEntity.layer.toLowerCase()) || firstLines.includes(someEntity) || !!this.excludedSearchIfContains.find(e => someEntity.layer.toLowerCase().includes(e))) {
                    return false;
                }
                let allowed = true;

                this.allowedTypes.forEach(allowedType => {
                    if (someEntity.name?.substring(0, allowedType.length) === allowedType.toLowerCase() && !this.endPointSpecificNames.includes(someEntity.name)) {
                        allowed = false;
                    }
                });
                if (bord.handle === someEntity.parent_handle || this.bordEntities.map(b => b.handle).includes(someEntity?.parent_handle)) {
                    return false;
                }
                if (!allowed) {
                    return false;
                }
                if (someEntity?.handle == searchForEntity.handle || someEntity?.handle == prevConnection?.handle) {
                    return false;
                }

                if (['LWPOLYLINE', 'LINE', 'HATCH', 'CIRCLE'].includes(someEntity?.type)) {
                    if (searchForEntity.polygon?.geometry?.coordinates?.length) {
                        let found = false;

                        if (searchForEntity.polygon && someEntity.polygon) {
                            try {
                                if (turf.booleanIntersects(searchForEntity.polygon, someEntity.polygon)) {
                                    found = true;
                                }
                            } catch (e) {
                                console.error(e);
                            }
                        }

                        return found;
                    } else if (searchForEntity.lat && searchForEntity.lng) {
                        return someEntity.polygon && turf.booleanPointInPolygon(turf.point([searchForEntity.lng, searchForEntity.lat]), someEntity.polygon);
                    }
                }
                if (someEntity?.type === 'INSERT' && someEntity.lat && this.endPointSpecificNames.includes(someEntity.name)) {
                    return searchForEntity.polygon && turf.booleanIntersects(someEntity.polygon, searchForEntity.polygon);
                }

                return false;
            });
        };

        let counter = 0;
        const recursiveFindConnections = (entity: ImportEntity, prevConnection) => {
            counter++;
            if (counter < 250) {
                findConnections(entity, prevConnection).forEach(foundEntity => {
                    if (!allFoundConnections.includes(foundEntity)) {
                        allFoundConnections.push(foundEntity);
                        recursiveFindConnections(foundEntity, entity);
                    }
                });
            }
        }

        recursiveFindConnections(inpEntity, bord);
        return allFoundConnections;
    }

    parallel(bord, otherBord) {
        const bordLine = turf.lineString([
            [bord.bord.block_max_lng, bord.bord.block_max_lat],
            [bord.bord.block_min_lng, bord.bord.block_min_lat]
        ]);
        const otherBordLine = turf.lineString([
            [otherBord.bord.block_max_lng, otherBord.bord.block_max_lat],
            [otherBord.bord.block_min_lng, otherBord.bord.block_min_lat]
        ]);
        const bordBearing = turf.bearing(bordLine.geometry.coordinates[0], bordLine.geometry.coordinates[1]);
        const otherBordBearing = turf.bearing(otherBordLine.geometry.coordinates[0], otherBordLine.geometry.coordinates[1]);
        return Math.abs(bordBearing - otherBordBearing) < 1;
    }

    distanceCm(
        pointA: { lng: number; lat: number; } | any,
        pointB: { lng: number; lat: number; } | any
    ): number {

        // Calculate distance in centimeters
        return this.distance(pointA, pointB, 'centimeters');
    }

    distanceM(
        pointA: { lng: number; lat: number; } | any,
        pointB: { lng: number; lat: number; } | any
    ): number {
        return this.distance(pointA, pointB, 'meters');
    }

    distance(
        pointA: { lng: number; lat: number; } | any,
        pointB: { lng: number; lat: number; } | any,
        units: 'centimeters' | 'meters'
    ): number {
        // Convert { lng, lat } objects to Turf Points if necessary
        const turfPointA =
            Array.isArray(pointA?.geometry?.coordinates) && typeof pointA?.geometry?.coordinates[0] === 'number' && typeof pointA?.geometry?.coordinates[1] === 'number'
                ? pointA
                : turf.point([pointA?.lng ?? pointA?.wgsStart?.lng, pointA.lat ?? pointA?.wgsStart?.lat]);
        const turfPointB =
            Array.isArray(pointB?.geometry?.coordinates) && typeof pointB?.geometry?.coordinates[0] === 'number' && typeof pointB?.geometry?.coordinates[1] === 'number'
                ? pointB
                : turf.point([pointB?.lng ?? pointB?.wgsStart?.lng, pointB?.lat ?? pointB?.wgsStart?.lat]);

        // Calculate distance in centimeters
        return turf.distance(turfPointA, turfPointB, {units: units});
    }

    lineFromGroupedLines(bord: MapEntity, allEntities: ImportEntity[]) {
        const parentFromLocation = allEntities.find(f => f.handle === bord.location.parent_handle);
        const isFence = parentFromLocation?.entities?.some(e => e.layer?.toLowerCase() === 'wy-kleur-010');
        const points = [];
        const lines = [];
        const firstline = bord.firstConnection;
        let tryingLines = bord.connections?.filter(c => !!c.polygon && c.layer !== 'wy-kleur-010');
        if (!tryingLines) {
            if (bord.firstConnection.wgsPoints) {
                return turf.lineString(bord.firstConnection.wgsPoints.map(f => [f.lng, f.lat]));
            } else {
                return null;
            }
        }
        let handleLine: ImportEntity = firstline;
        do {
            for (let a = 0; a < tryingLines.length; a++) {
                let c = tryingLines[a];
                if (c?.polygon && handleLine?.polygon && turf.booleanIntersects(c.polygon, handleLine.polygon)) {
                    handleLine = c;
                    lines.push(handleLine);
                    tryingLines.splice(tryingLines.indexOf(c), 1);
                    break;
                }
                if (handleLine?.handle === bord.location.handle) {
                    tryingLines.length = 0;
                    break;
                }
                tryingLines.splice(tryingLines.indexOf(handleLine), 1);
                handleLine = lines[lines.length - 1];
            }
        } while (tryingLines.length > 0);

        lines.forEach(line => {
            if (line.wgsPoints) {
                if (points?.length && turf.distance(turf.point([line.wgsStart.lng, line.wgsStart.lat]), turf.point(points[points.length - 1]), {units: 'centimeters'}) > 50) {
                    points.push(...line.wgsPoints.map((p) => [p.lng, p.lat]).reverse());
                } else {
                    points.push(...line.wgsPoints.map((p) => [p.lng, p.lat]));
                }
            }
        });
        if (bord.location?.lng) {
            points.push([bord.location.lng, bord.location.lat]);
        }
        const firstConnectionIsPole = bord.firstConnection && bord.firstConnection.type === 'LWPOLYLINE' && bord.firstConnection.start[2] === 0.6 && bord.firstConnection.start[3] === 0.6;
        if (!firstConnectionIsPole) {
            if (points?.length) {
                const distanceLineToLocation1 = turf.distance(turf.point([bord.location?.lng ?? bord.location?.wgsStart?.lng, bord.location?.lat ?? bord.location?.wgsStart?.lat]), turf.point(points[0]), {units: 'centimeters'});
                const distanceLineToLocation2 = turf.distance(turf.point([bord.location?.lng ?? bord.location?.wgsEnd?.lng, bord.location?.lat ?? bord.location?.wgsEnd?.lat]), turf.point(points[0]), {units: 'centimeters'});
                if (isFence && Math.min(distanceLineToLocation1, distanceLineToLocation2) < (bord?.locationNr ?? 1) * 250) {
                    points.reverse();
                }
                const distanceFirstLineToFirstLinePoint = turf.distance(turf.point(points[0]), turf.point([firstline.wgsPoints[0].lat, firstline.wgsPoints[0].lng]), {units: 'centimeters'});
                if (distanceFirstLineToFirstLinePoint > 50) {
                    points.splice(0, 0, ...firstline.wgsPoints.map((p) => [p.lng, p.lat]).reverse());
                }
            }
        }
        if (firstline) {
            [firstline, ...parentFromLocation.entities
                .filter(c => !!c.polygon && !!c.points && !lines.map(l => l.handle).includes(c.handle) && c.handle !== firstline?.handle && !this.excludedSearch.includes(c.layer))]
                .forEach(c => {
                    const checkOn = points?.length ? points[0] : [firstline.wgsPoints[0].lng, firstline.wgsPoints[0].lat];
                    if (turf.booleanPointInPolygon(turf.point(checkOn), c.polygon)) { // kijken welke kant je moet hebben, niet de kant van de locatie
                        if (c.wgsPoints) {
                            if (points?.length && turf.distance(turf.point([c.wgsStart.lng, c.wgsStart.lat]), turf.point(checkOn), {units: 'centimeters'}) > 50) {
                                points.unshift(...c.wgsPoints.map((p) => [p.lng, p.lat]).reverse());
                            } else {
                                points.unshift(...c.wgsPoints.map((p) => [p.lng, p.lat]).reverse());
                            }
                        }
                    }
                });
        }
        return points?.length > 1 ? turf.cleanCoords(turf.lineString(points)) : null;
    }

    extractLineFromConnections(
        connections: ImportEntity[],
        bord: ImportEntity,
        location: ImportEntity,
        firstConnection: ImportEntity
    ): Feature<LineString> | null {

        const ll: Feature<LineString>[] = [];

        // Maak een FeatureCollection van de lijnen
        connections.forEach((connection) => {
            if (['LWPOLYLINE', 'LINE'].includes(connection?.type || '') && connection.wgsPoints) {
                const line = turf.lineString(
                    connection.wgsPoints.map((p) => [p.lng, p.lat]),
                    {id: connection.type}
                );
                ll.push(line);
            }
        });

        if (ll.length === 0) {
            console.warn('Geen lijnen gevonden.');
            return null;
        }

        const lines: FeatureCollection<LineString> = turf.featureCollection(ll);

        // Buffer de lijnen voor overlapcontrole
        const bufferedPolygons: Feature<Polygon | MultiPolygon>[] = lines.features.map((line) =>
            turf.buffer(line, 0.22, {units: 'meters'}) as Feature<Polygon | MultiPolygon>
        );

        // Definieer start- en eindpunten


        const start: Feature<Point> = turf.point([firstConnection.wgsEnd.lng!, firstConnection.wgsEnd.lat!]);
        const end: Feature<Point> = turf.point([location?.lng ?? location.wgsStart?.lng!, location?.lat ?? location.wgsStart?.lat!]);

        // Bouw een graafstructuur
        function buildGraph(lines: FeatureCollection<LineString>, buffers: Feature<Polygon | MultiPolygon>[]) {
            const graph: Record<string, { index: number; target: string }[]> = {};

            lines.features.forEach((line, index) => {
                const coords = line.geometry.coordinates;
                const start = JSON.stringify(coords[0]);
                const end = JSON.stringify(coords[coords.length - 1]);

                if (!graph[start]) graph[start] = [];
                if (!graph[end]) graph[end] = [];

                // Controleer overlap met gebufferde lijnen
                buffers.forEach((buffer, bufferIndex) => {
                    if (index !== bufferIndex && turf.booleanIntersects(line, buffer)) {
                        const bufferCoords = lines.features[bufferIndex].geometry.coordinates;
                        const bufferStart = JSON.stringify(bufferCoords[0]);
                        const bufferEnd = JSON.stringify(bufferCoords[bufferCoords.length - 1]);

                        if (!graph[start].some((item) => item.target === bufferStart)) {
                            graph[start].push({index: bufferIndex, target: bufferStart});
                        }
                        if (!graph[end].some((item) => item.target === bufferEnd)) {
                            graph[end].push({index: bufferIndex, target: bufferEnd});
                        }
                    }
                });
            });

            return graph;
        }

        // Kortste pad algoritme (Dijkstra)
        function findShortestPath(
            graph: Record<string, { index: number; target: string }[]>,
            start: string,
            end: string
        ): number[] {
            const queue: string[] = [start];
            const visited: Set<string> = new Set();
            const paths: Record<string, number[]> = {[start]: []};

            while (queue.length > 0) {
                const current = queue.shift()!;
                if (current === end) return paths[current];
                if (visited.has(current)) continue;
                visited.add(current);

                for (const neighbor of graph[current] || []) {
                    if (!visited.has(neighbor.target)) {
                        queue.push(neighbor.target);
                        paths[neighbor.target] = [...paths[current], neighbor.index];
                    }
                }
            }
            return [];
        }

        const graph = buildGraph(lines, bufferedPolygons);

        const combinedLines = turf.combine(lines).features[0] as Feature<LineString | MultiLineString>;

        // Vind dichtstbijzijnde punten op gecombineerde lijnen
        // soms een rare fout op coordinaten, geen fout te vinden, vandaar try, catch
        try {
            const nearestStart = turf.nearestPointOnLine(combinedLines, start) as Feature<Point>;
            const nearestEnd = turf.nearestPointOnLine(combinedLines, end) as Feature<Point>;

            // Gebruik JSON.stringify voor consistentie met de graaf
            const path = findShortestPath(
                graph,
                JSON.stringify(nearestStart.geometry.coordinates),
                JSON.stringify(nearestEnd.geometry.coordinates)
            );

            // Controleer of er een volledig pad is gevonden
            if (path.length === 0) {
                console.warn('Geen compleet pad gevonden tussen start en eindpunt.');
                return null;
            }

            // Haal de gebruikte lijnen op
            const usedLines = path.map((index) => lines.features[index]);

            // Gebruik mergeLines om de lijnen samen te voegen tot één lijn


            const mergedLine = this.mergeLines(usedLines);

            // Controleer of de laatste coördinaat overeenkomt met het eindpunt
            if (!mergedLine || !mergedLine.geometry.coordinates.some(coord =>
                coord[0] === nearestEnd.geometry.coordinates[0] && coord[1] === nearestEnd.geometry.coordinates[1])) {
                console.warn('Het pad bereikt het eindpunt niet.');
                return null;
            }

            return mergedLine;
        } catch (e) {
            return null;
        }
    }

    mergeLines(lines: Feature<LineString>[]): Feature<LineString> | null {
        if (lines.length === 0) {
            console.warn('Geen lijnen om samen te voegen.');
            return null;
        }

        // Sorteer de lijnen zodat ze een aaneengesloten lijn vormen
        const sortedLines: Feature<LineString>[] = [];
        const usedIndices = new Set<number>();

        sortedLines.push(lines[0]);
        usedIndices.add(0);

        while (sortedLines.length < lines.length) {
            const lastLine = sortedLines[sortedLines.length - 1];
            const lastCoords = lastLine.geometry.coordinates;

            const lastPoint = lastCoords[lastCoords.length - 1];

            // Zoek de volgende lijn die aansluit
            let found = false;
            for (let i = 0; i < lines.length; i++) {
                if (usedIndices.has(i)) continue;

                const currentLine = lines[i];
                const currentCoords = currentLine.geometry.coordinates;

                if (
                    currentCoords[0][0] === lastPoint[0] &&
                    currentCoords[0][1] === lastPoint[1]
                ) {
                    // De huidige lijn begint waar de vorige eindigt
                    sortedLines.push(currentLine);
                    usedIndices.add(i);
                    found = true;
                    break;
                } else if (
                    currentCoords[currentCoords.length - 1][0] === lastPoint[0] &&
                    currentCoords[currentCoords.length - 1][1] === lastPoint[1]
                ) {
                    // De huidige lijn eindigt waar de vorige eindigt, draai hem om
                    const reversedCoords = [...currentCoords].reverse();
                    sortedLines.push(turf.lineString(reversedCoords));
                    usedIndices.add(i);
                    found = true;
                    break;
                }
            }

            if (!found) {
                console.warn('Niet alle lijnen zijn aansluitend.');
                break;
            }
        }

        // Combineer de coördinaten
        const mergedCoords: number[][] = [];
        sortedLines.forEach((line, index) => {
            const coords = line.geometry.coordinates;
            if (index > 0) {
                // Vermijd duplicaatpunt bij overgang tussen lijnen
                coords.shift();
            }
            mergedCoords.push(...coords);
        });

        // Maak een enkele lijn van de gecombineerde coördinaten
        return turf.lineString(mergedCoords);
    }


    mapEntityToEntityToSave(mapEntity: MapEntity) {
        const entity = new EntityToSave();
        entity.handle = mapEntity.bord.handle;
        entity.name = mapEntity.bord.name;
        entity.polygon = mapEntity.bord.original_polygon ?? mapEntity.bord.polygon;
        if (entity.polygon) {
            entity.polygon = simplify(entity.polygon, {tolerance: 0.00001})?.geometry?.coordinates[0];
        }

        if (mapEntity.bord.type.endsWith('TEXT')) {
            entity.line = mapEntity.lineToLocation?.geometry.coordinates;
        }
        if (mapEntity.bord.type === 'HATCH' && mapEntity.bord.polygon) {
            if (mapEntity.bord.polygon?.features?.length) {
                entity.line = mapEntity.bord.polygon?.features[0].geometry.coordinates[0];
            } else if (mapEntity.bord.polygon?.geometry?.coordinates?.length) {
                entity.line = mapEntity.bord.polygon?.geometry.coordinates[0];
            }
        } else if (mapEntity.bord.type === 'LWPOLYLINE') {
            if (mapEntity.bord.wgsPoints?.length) {
                entity.line = mapEntity.bord.wgsPoints.map(l => [l.lng, l.lat]) as any;
            }
            if (mapEntity.bord.color) {
                entity.color = this.rgbToHex(mapEntity.bord.color[0], mapEntity.bord.color[1], mapEntity.bord.color[2]);
            }
        } else {
            entity.bbox = [
                [Math.min(mapEntity.bord.block_min_lng, mapEntity.bord.block_max_lng), Math.min(mapEntity.bord.block_min_lat, mapEntity.bord.block_max_lat)],
                [Math.max(mapEntity.bord.block_min_lng, mapEntity.bord.block_max_lng), Math.max(mapEntity.bord.block_min_lat, mapEntity.bord.block_max_lat)]
            ] as any;
        }

        if (mapEntity.bord.block_min && mapEntity.bord.block_max) {
            entity.width = Math.round(Math.abs(mapEntity.bord.block_min[0] - mapEntity.bord.block_max[0]));
            entity.height = Math.round(Math.abs(mapEntity.bord.block_min[1] - mapEntity.bord.block_max[1]));
        }

        return entity;
    }

    rgbToHex(r, g, b) {
        return '#' + (1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1);
    }

    getTtmsAndOthers() {
        const otherItems = this.otherItemsForExport.map(other => {
            const entity = this.mapEntityToEntityToSave(other);

            if (other.location?.lat) {
                entity.gnd = [[other.location?.lng, other.location?.lat]] as any;
            } else if (other.bord?.wgsPoints?.length) {
                entity.gnd = [[other.bord.wgsPoints[0]?.lng, other.bord.wgsPoints[0]?.lat]] as any;
            }

            entity.attr = other.bord.attr;
            entity.scale_x = other.bord.xScale;
            entity.scale_y = other.bord.yScale;
            entity.rotation = other.bord.rotation;

            return entity;
        }).filter(e => !!e.gnd);

        const entities = this.bordenForExport.map(bord => {
            const entity = this.mapEntityToEntityToSave(bord);
            entity.belongsToHandle = bord?.belongsToEntity?.bord?.handle;
            if (bord.bord?.type === 'LWPOLYLINE') {
                entity.gnd = [[bord.bord.wgsPoints[0]?.lng, bord.bord.wgsPoints[0]?.lat]] as any;
            } else if (bord.bord?.type === 'HATCH' && bord.bord.wgsLines?.length) {
                entity.gnd = [[bord.bord.wgsLines[0][0]?.lng, bord.bord.wgsLines[0][0]?.lat]] as any;
            } else {
                if (['INSERT', 'MTEXT'].includes(bord.location?.type)) {
                    if (this.endPointSpecificNames.includes(bord.location.name) || this.referenceNr.includes(bord.location.name)) {
                        entity.gnd_handle = bord.location.handle;
                        entity.gnd = [
                            [Math.min(bord.location.block_min_lng, bord.location.block_max_lng), Math.min(bord.location.block_min_lat, bord.location.block_max_lat)],
                            [Math.max(bord.location.block_min_lng, bord.location.block_max_lng), Math.max(bord.location.block_min_lat, bord.location.block_max_lat)]
                        ] as any;
                    } else {
                        entity.gnd = [[bord.location?.lng, bord.location?.lat]] as any;
                    }
                } else if (bord.location) {
                    if (bord.multiLocation && Array.isArray(bord.locations)) {
                        entity.gnd = bord.locations as any;
                    } else if (Array.isArray(bord.location)) {
                        entity.gnd = bord.location as any;
                    } else if (bord.location.wgsPoints?.length && (bord.location.wgsPoints?.length === 2)) {
                        if ([TtmTypes.closures, TtmTypes.matrix_signs].includes(bord.ttmType)) {
                            entity.gnd = bord.location.wgsPoints.map(l => [l.lng, l.lat]) as any;
                        } else {
                            entity.gnd = bord.location.wgsPoints.slice(0, 1).map(l => [l.lng, l.lat]) as any;
                        }
                    } else {
                        entity.gnd = [[bord.location?.lng, bord.location?.lat]] as any;
                    }
                }
                entity.line = bord.lineToLocation?.geometry.coordinates;
            }
            if (bord.firstConnection && bord.firstConnection.type === 'LWPOLYLINE' && bord.firstConnection.start[2] === 0.6 && bord.firstConnection.start[3] === 0.6) {
                entity.pole = (bord.firstConnection as ImportEntity)?.wgsPoints?.map(l => [l.lng, l.lat]) as any;
                if (bord.firstConnection && !entity.pole) {
                    if ((bord.firstConnection)?.geometry?.coordinates) {
                        entity.pole = (bord.firstConnection).geometry.coordinates.map(c => [c[0], c[1]]);
                    } else {
                        console.info((bord.firstConnection));
                    }
                }
            }
            if (bord.firstConnection && bord.firstConnection.type === 'LWPOLYLINE' && bord.firstConnection.points.filter(p => p[2] >= 0.5 && p[3] >= 0.5).length > 2) {
                entity.pole = (bord.firstConnection as ImportEntity)?.wgsPoints?.reverse().slice(0, 2).map(l => [l.lng, l.lat]) as any;
            }

            entity['bord'] = bord;
            entity.attr = bord.bord.attr ?? {};
            if (bord.bord.moved) {
                entity.attr = {...entity.attr, moved: bord.bord.moved};
                delete entity.attr.moved.boundary;
            }
            entity.attr['config'] = bord.bord.config;
            if (bord.bord?.type === 'MTEXT') {
                entity.attr['text'] = bord.bord.text;
            }
            entity.scale_x = bord.bord.xScale;
            entity.scale_y = bord.bord.yScale;
            entity.rotation = bord.bord.rotation;
            entity.type = bord.ttmType;
            return entity;
        }).filter(e => !!e.gnd && !!e.gnd[0][0] && (!e.bbox || !!e.bbox[0][0] || !!e.gnd_handle));

        const ttms = [];

        entities.filter(e => !e.belongsToHandle && !e.bord.group).forEach(entity => {
            const ttm = new Ttm();
            ttm.entities = [];
            ttm.entities.push(entity);
            ttm.entities.push(...entities.filter(e => e.belongsToHandle === entity.handle));
            ttm.type_key = entity.bord.ttmType;
            ttm.status_key = entity.bord.ttmStatus;
            ttms.push(ttm);
        });
        let groupName = '';
        entities.filter(e => !!e?.bord?.group).forEach(entity => {
            if (entity.bord.group !== groupName) {
                groupName = entity.bord.group;
                const ttm = new Ttm();
                ttm.entities = [];
                ttm.entities.push(entity);
                ttm.entities.push(...entities.filter(e => e?.bord?.group === entity.bord.group));
                ttm.type_key = entity.bord.ttmType;
                ttm.status_key = entity.bord.ttmStatus;
                ttms.push(ttm);
            }
        });

        ttms.forEach(ttm => {
            ttm.entities.forEach(e => {
                if (ttm.type_key === TtmTypes.permanent_signs) {
                    ttm.status_key = 'permanent';
                }
                delete e['bord']; // wordt tijdelijk gebruik voor bundeling
            });
        });
        return {ttms, otherItems, xRefs: this.xrefs};
    }

}

export class MovedCoordinatesBoundary {
    movedX: number;
    movedY: number;
    boundary: number[][];
}

export class ExcludedCoordinatesBoundary {
    boundary: number[][];
}
